import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { END_POINT } from "../../../constants/defaultValues";
import {
  TAX_BRACKETS_2026, TAX_BRACKETS_2025,
  DEDUCTION_2026, DEDUCTION_2025,
  INSURANCE_RATE, BIZ_TAX_EXEMPT, BIZ_TAX_QUARTER_EXEMPT, BIZ_INDUSTRIES,
  DEADLINES, GUIDE_STEPS, GROUP_LABELS,
  QUARTER_LABELS, MONTHS_BY_QUARTER,
} from "../../../constants/taxConstants";
import {
  fmt, fmtM, calcTax,
  calcSalaryTax, calcGrossFromNet as calcGrossFromNetFn,
  calcBusinessTax, calcEnterpriseTax,
  calcTkData, calcSoSummary, calcEntryTax,
} from "../../../helpers/taxCalculator";
import {
  formatDateVN, formatNumber,
  SO_TITLES, SO_MAU, PDF_CSS,
  openPrintWindow,
  xlsxHeaderRows, xlsxFooterRows, buildAndDownloadXlsx,
} from "../../../helpers/taxExportHelpers";

const tokenStr = () => localStorage.getItem("access_token");
const authHeaders = () => ({ Authorization: `Bearer ${tokenStr()}` });
const toMoneyNumber = (value) => {
  if (typeof value === "number") return value;
  if (typeof value === "string") return parseInt(value.replace(/\D/g, ""), 10) || 0;
  return 0;
};
const addDaysISO = (dateText, days) => {
  const d = dateText ? new Date(dateText) : new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().split("T")[0];
};
const getNextSoDraftDate = (entries) => {
  const latest = entries
    .map(e => e.date)
    .filter(Boolean)
    .sort()
    .pop();
  return latest ? addDaysISO(latest, 1) : new Date().toISOString().split("T")[0];
};
const soEntriesStorageKey = (bookType) => `vmass_tax_so_entries_${bookType}`;
const getStoredSoEntries = (bookType) => {
  try {
    const raw = localStorage.getItem(soEntriesStorageKey(bookType));
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    console.error("getStoredSoEntries", e);
    return null;
  }
};
const storeSoEntries = (bookType, entries) => {
  try {
    localStorage.setItem(soEntriesStorageKey(bookType), JSON.stringify(entries));
  } catch (e) {
    console.error("storeSoEntries", e);
  }
};

// ====== SHARED COMPONENTS ======
const tabs = [
  { id: "hkd", icon: "🏪", label: "Hộ KD" },
  { id: "calc", icon: "💼", label: "NLĐ" },
  { id: "dn", icon: "🏢", label: "Doanh nghiệp" },
  { id: "guide", icon: "📋", label: "Hướng dẫn" },
  { id: "calendar", icon: "📅", label: "Lịch thuế" },
];

function MoneyInput({ value, onChange, placeholder, label, suffix }) {
  const [display, setDisplay] = useState(value ? fmt(value) : "");
  useEffect(() => {
    if (value === 0 || value === "") setDisplay("");
    else if (value) setDisplay(fmt(value));
  }, [value]);
  const handleChange = (e) => {
    const raw = e.target.value.replace(/[^\d]/g, "");
    if (raw === "") { setDisplay(""); onChange(0); return; }
    const num = parseInt(raw, 10);
    setDisplay(fmt(num));
    onChange(num);
  };
  return (
    <div style={{ marginBottom: 12 }}>
      {label && <label style={{ display: "block", fontSize: 13, color: "#8B7355", marginBottom: 4, fontWeight: 500 }}>{label}</label>}
      <div style={{ position: "relative" }}>
        <input type="text" inputMode="numeric" value={display} onChange={handleChange} placeholder={placeholder || "0"} style={{ width: "100%", padding: "10px 50px 10px 14px", border: "1.5px solid #D4C4A8", borderRadius: 10, fontSize: 16, fontFamily: "'DM Mono', monospace", background: "#FFFDF7", color: "#3D2E1F", outline: "none", boxSizing: "border-box", transition: "border-color 0.2s" }} onFocus={(e) => e.target.style.borderColor = "#B8860B"} onBlur={(e) => e.target.style.borderColor = "#D4C4A8"} />
        <span style={{ position: "absolute", right: 14, top: "50%", transform: "translateY(-50%)", color: "#B8860B", fontWeight: 600, fontSize: 13 }}>{suffix || "VNĐ"}</span>
      </div>
    </div>
  );
}

function ResultCard({ label, value, sub, accent, color }) {
  return (
    <div style={{ background: accent ? (color || "linear-gradient(135deg, #B8860B 0%, #DAA520 100%)") : "#FFFDF7", borderRadius: 14, padding: "14px 16px", border: accent ? "none" : "1px solid #E8DCC8", marginBottom: 8 }}>
      <div style={{ fontSize: 12, color: accent ? "rgba(255,255,255,0.8)" : "#8B7355", marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 700, color: accent ? "#FFF" : "#3D2E1F", fontFamily: "'DM Mono', monospace" }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: accent ? "rgba(255,255,255,0.7)" : "#A0896C", marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

function BracketBar({ breakdown, total }) {
  if (!breakdown.length) return null;
  const colors = ["#DAA520", "#B8860B", "#CD853F", "#A0522D", "#8B4513"];
  return (
    <div style={{ marginTop: 12 }}>
      <div style={{ display: "flex", borderRadius: 8, overflow: "hidden", height: 28 }}>
        {breakdown.map((b, i) => {
          const pct = total > 0 ? (b.tax / total) * 100 : 0;
          if (pct < 1) return null;
          return (<div key={i} style={{ width: pct + "%", background: colors[i % colors.length], display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, color: "#FFF", fontWeight: 600, transition: "width 0.5s ease" }}>{pct > 12 ? `${(b.rate * 100)}%` : ""}</div>);
        })}
      </div>
      <div style={{ marginTop: 8 }}>
        {breakdown.map((b, i) => (
          <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "4px 0", borderBottom: i < breakdown.length - 1 ? "1px solid #F0E8D8" : "none" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <div style={{ width: 10, height: 10, borderRadius: 3, background: colors[i % colors.length] }} />
              <span style={{ fontSize: 12, color: "#5D4E37" }}>{b.rate * 100}%</span>
              <span style={{ fontSize: 11, color: "#A0896C" }}>({fmtM(b.taxable)})</span>
            </div>
            <span style={{ fontSize: 13, fontWeight: 600, color: "#3D2E1F", fontFamily: "'DM Mono', monospace" }}>{fmt(b.tax)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ====== MAIN APP ======
export default function TaxApp() {
  const [activeTab, setActiveTab] = useState("hkd");
  const [grossIncome, setGrossIncome] = useState(0);
  const [salaryMode, setSalaryMode] = useState("gross"); // "gross" or "net"
  const [netInput, setNetInput] = useState(0);
  const [dependents, setDependents] = useState(0);
  const [otherDeductions, setOtherDeductions] = useState(0);
  const [insuranceSalary, setInsuranceSalary] = useState(0);
  const [useCustomInsurance, setUseCustomInsurance] = useState(false);
  const [contractType, setContractType] = useState("long"); // long = ≥3 tháng, short = <3 tháng
  const [allowanceMeal, setAllowanceMeal] = useState(0);
  const [allowanceOther, setAllowanceOther] = useState(0);
  const [bonus13, setBonus13] = useState(0);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [guideType, setGuideType] = useState("salary");
  const [expandedStep, setExpandedStep] = useState(null);
  // DN state
  const [dnRevenue, setDnRevenue] = useState(0);
  const [dnCost, setDnCost] = useState(0);
  const [dnOtherIncome, setDnOtherIncome] = useState(0);
  const [dnExemptIncome, setDnExemptIncome] = useState(0);
  const [dnLossCarry, setDnLossCarry] = useState(0);
  const [dnVatMethod, setDnVatMethod] = useState("deduct"); // deduct or direct
  const [dnVatOut, setDnVatOut] = useState(0);
  const [dnVatIn, setDnVatIn] = useState(0);
  const [bizRevenue, setBizRevenue] = useState(0);
  const [bizIndustry, setBizIndustry] = useState("trade");
  const [bizMethod, setBizMethod] = useState("revenue");
  const [bizCost, setBizCost] = useState(0);
  const [hkdSubTab, setHkdSubTab] = useState("tinhtoan"); // "tinhtoan" or "tokhai" or "sosach"
  // Sổ sách state
  const [soType, setSoType] = useState("s1a");
  const [soEntries, setSoEntries] = useState([]);
  const [soEntriesStorageReady, setSoEntriesStorageReady] = useState(false);
  const [soDraft, setSoDraft] = useState({ date: new Date().toISOString().split("T")[0], desc: "Doanh thu bán hàng", amount: 0 });
  // S2c: fixed structure - doanh thu + 5 loại chi phí
  const [s2cData, setS2cData] = useState({ revenue: 0, cpA: 0, cpB: 0, cpC: 0, cpD: 0, cpE: 0 });
  const s2cTotalCost = s2cData.cpA + s2cData.cpB + s2cData.cpC + s2cData.cpD + s2cData.cpE;
  const s2cProfit = Math.max(0, s2cData.revenue - s2cTotalCost);
  // S2e: tiền mặt + tiền gửi NH
  const [s2eData, setS2eData] = useState({ cashOpen: 0, cashIn: 0, cashOut: 0, banks: [{ name: "", open: 0, in: 0, out: 0 }] });
  const s2eCashClose = s2eData.cashOpen + s2eData.cashIn - s2eData.cashOut;
  const addS2eBank = () => setS2eData({...s2eData, banks: [...s2eData.banks, { name: "", open: 0, in: 0, out: 0 }]});
  const updateS2eBank = (idx, field, val) => { const b = [...s2eData.banks]; b[idx] = {...b[idx], [field]: val}; setS2eData({...s2eData, banks: b}); };
  const removeS2eBank = (idx) => setS2eData({...s2eData, banks: s2eData.banks.filter((_,i) => i !== idx)});
  const newAmountRef = useRef(null);
  const addSoEntry = () => {
    const amount = toMoneyNumber(soDraft.amount);
    if (!soDraft.date || amount <= 0) return;
    const entryInd = BIZ_INDUSTRIES.find(x => x.id === bizIndustry);
    const vatAmount = (soType === "s2a" || soType === "s2b") ? Math.round(amount * (entryInd?.vatRate || 0.01)) : 0;
    const pitAmount = soType === "s2a" ? Math.round(amount * (entryInd?.pitRate || 0.005)) : 0;
    const newEntry = {
      date: soDraft.date || new Date().toISOString().split("T")[0],
      desc: soDraft.desc || "Doanh thu bán hàng",
      amount,
      vatAmount,
      pitAmount,
      cost: 0,
      note: "",
      industry: bizIndustry,
      isNew: true,
    };
    setSoEntries([newEntry, ...soEntries.map(e => ({ ...e, isNew: false }))]);
    setSoDraft(prev => ({ ...prev, date: addDaysISO(prev.date, 1), desc: "Doanh thu bán hàng", amount: 0 }));
    setTimeout(() => newAmountRef.current?.focus(), 0);
    setTimeout(() => setSoEntries(prev => prev.map(e => ({ ...e, isNew: false }))), 2000);
  };
  const updateSoEntry = (idx, field, val) => {
    const n = [...soEntries];
    n[idx] = { ...n[idx], [field]: val };
    // Auto-calc tax for S2a (GTGT+TNCN) and S2b (GTGT only)
    if ((soType === "s2a" || soType === "s2b") && (field === "amount" || field === "industry")) {
      const entryInd = BIZ_INDUSTRIES.find(x => x.id === (field === "industry" ? val : n[idx].industry || bizIndustry));
      const amt = field === "amount" ? val : (n[idx].amount || 0);
      n[idx].vatAmount = Math.round(amt * (entryInd?.vatRate || 0.01));
      if (soType === "s2a") n[idx].pitAmount = Math.round(amt * (entryInd?.pitRate || 0.005));
    }
    setSoEntries(n);
  };
  const removeSoEntry = (idx) => setSoEntries(soEntries.filter((_, i) => i !== idx));
  const removeSoEntries = (indexes) => {
    const removeSet = new Set(indexes);
    setSoEntries(soEntries.filter((_, i) => !removeSet.has(i)));
  };
  const soTotal = soEntries.reduce((s, e) => s + toMoneyNumber(e.amount), 0);
  const soCostTotal = soEntries.reduce((s, e) => s + toMoneyNumber(e.cost), 0);
  const soVatTotal = soEntries.reduce((s, e) => s + toMoneyNumber(e.vatAmount), 0);
  const soPitTotal = soEntries.reduce((s, e) => s + toMoneyNumber(e.pitAmount), 0);
  // S2a: group by industry for summary
  const soByIndustry = soType === "s2a" ? BIZ_INDUSTRIES.map(ind => {
    const items = soEntries.filter(e => (e.industry || bizIndustry) === ind.id);
    if (items.length === 0) return null;
    return { ...ind, count: items.length, total: items.reduce((s,e) => s + toMoneyNumber(e.amount), 0), vat: items.reduce((s,e) => s + toMoneyNumber(e.vatAmount), 0), pit: items.reduce((s,e) => s + toMoneyNumber(e.pitAmount), 0) };
  }).filter(Boolean) : [];
  const soEntriesByMonth = (() => {
    const groups = [];
    const byKey = {};
    soEntries.forEach((entry, idx) => {
      const dateText = entry.date || new Date().toISOString().split("T")[0];
      const [yearPart, monthPart] = dateText.split("-");
      const d = new Date(dateText);
      const year = yearPart || d.getFullYear();
      const month = monthPart || String(d.getMonth() + 1).padStart(2, "0");
      const key = `${year}-${month}`;
      if (!byKey[key]) {
        byKey[key] = {
          key,
          label: `Tháng ${month}/${year}`,
          entries: [],
          rows: [],
          dayMap: {},
          total: 0,
          vat: 0,
          pit: 0,
        };
        groups.push(byKey[key]);
      }
      byKey[key].entries.push({ ...entry, idx });
      byKey[key].total += toMoneyNumber(entry.amount);
      byKey[key].vat += toMoneyNumber(entry.vatAmount);
      byKey[key].pit += toMoneyNumber(entry.pitAmount);
      if (!byKey[key].dayMap[dateText]) {
        const dayRow = {
          key: `${key}-${dateText}`,
          date: dateText,
          desc: "",
          amount: 0,
          vatAmount: 0,
          pitAmount: 0,
          cost: 0,
          note: "",
          unit: "",
          inQty: 0,
          outQty: 0,
          industry: "",
          mixedIndustry: false,
          indexes: [],
          isNew: false,
          lineCount: 0,
          descList: [],
          industryList: [],
          noteList: [],
          unitList: [],
        };
        byKey[key].dayMap[dateText] = dayRow;
        byKey[key].rows.push(dayRow);
      }
      const row = byKey[key].dayMap[dateText];
      row.indexes.push(idx);
      row.lineCount += 1;
      row.amount += toMoneyNumber(entry.amount);
      row.vatAmount += toMoneyNumber(entry.vatAmount);
      row.pitAmount += toMoneyNumber(entry.pitAmount);
      row.cost += toMoneyNumber(entry.cost);
      row.inQty += toMoneyNumber(entry.inQty);
      row.outQty += toMoneyNumber(entry.outQty);
      row.isNew = row.isNew || !!entry.isNew;
      if (entry.desc && !row.descList.includes(entry.desc)) row.descList.push(entry.desc);
      if ((entry.industry || bizIndustry) && !row.industryList.includes(entry.industry || bizIndustry)) row.industryList.push(entry.industry || bizIndustry);
      if (entry.note && !row.noteList.includes(entry.note)) row.noteList.push(entry.note);
      if (entry.unit && !row.unitList.includes(entry.unit)) row.unitList.push(entry.unit);
    });
    groups.forEach(group => {
      group.rows.forEach(row => {
        row.desc = row.descList.join(", ") || "Doanh thu bán hàng";
        row.industry = row.industryList.length === 1 ? row.industryList[0] : "";
        row.mixedIndustry = row.industryList.length > 1;
        row.note = row.noteList.length === 1 ? row.noteList[0] : row.lineCount > 1 ? `${row.lineCount} dòng` : "";
        row.unit = row.unitList.length === 1 ? row.unitList[0] : "";
      });
      delete group.dayMap;
    });
    return groups;
  })();

  const downloadSoXlsx = () => {
    const ind2 = BIZ_INDUSTRIES.find(x => x.id === bizIndustry);
    const fD = formatDateVN;
    const sorted = [...soEntries].reverse();
    const vatP = ((ind2?.vatRate || 0) * 100);
    const title = SO_TITLES[soType] || "SỔ";
    const mau = SO_MAU[soType] || soType;
    const businessInfo = { hoTen: tkHoTen, diaChiKD: tkDiaChiKD, mst: tkMST };
    let dataRows = [];
    let colWidths = [];

    if (soType === "s1a") {
      const N = null;
      dataRows = [
        // Header trái + phải
        [`HỘ, CÁ NHÂN KINH DOANH: ${tkHoTen || "......"}`, N, "Mẫu số S1a-HKD"],
        [`Mã số thuế: ${tkMST || "......................................"}`, N, "(Kèm theo Thông tư số 152/2025/TT-BTC"],
        [`Địa chỉ: ${tkDiaChiKD || tkDiaChi || "............................................"}`, N, "ngày 31 tháng 12 năm 2025 của Bộ trưởng Bộ"],
        [N, N, "Tài chính)"],
        [N, N, N],
        // Tiêu đề
        [N, "SỔ CHI TIẾT DOANH THU BÁN HÀNG HÓA, DỊCH VỤ", N],
        [N, `Địa điểm kinh doanh: ${tkDiaChiKD || ".............................."}`, N],
        [N, `Kỳ kê khai: Năm 2026`, N],
        [N, N, N],
        // Bảng dữ liệu
        ["Ngày tháng", "Giao dịch", "Số tiền"],
        ["A", "B", "1"],
        ...sorted.map(e => [fD(e.date), e.desc, e.amount || 0]),
        [N, "Tổng cộng", soTotal],
        [N, N, N],
        // Chữ ký
        [N, N, "Ngày ... tháng ... năm ..."],
        [N, N, "NGƯỜI ĐẠI DIỆN HỘ KINH DOANH/"],
        [N, N, "CÁ NHÂN KINH DOANH"],
        [N, N, "(Ký, họ tên, đóng dấu)"],
      ];
      colWidths = [15, 45, 25];
    }
    else if (soType === "s2a") {
      const N = null;
      const groupRows = [];
      BIZ_INDUSTRIES.forEach((ind, gi) => {
        const items = sorted.filter(e => (e.industry || bizIndustry) === ind.id);
        if (items.length === 0) return;
        const gTotal = items.reduce((s, e) => s + (e.amount || 0), 0);
        const gTax = items.reduce((s, e) => s + (e.vatAmount || 0) + (e.pitAmount || 0), 0);
        groupRows.push([`${gi + 1}. Ngành nghề: ${ind.label}`, N, N, N, N]);
        items.forEach(e => groupRows.push([e.note || "", fD(e.date), e.desc, e.amount || 0, (e.vatAmount || 0) + (e.pitAmount || 0)]));
        groupRows.push(["", "", `Tổng cộng (${gi + 1})`, gTotal, gTax]);
      });
      dataRows = [
        [`HỘ, CÁ NHÂN KINH DOANH: ${tkHoTen || "......"}`, N, N, N, "Mẫu số S2a-HKD"],
        [`Mã số thuế: ${tkMST || "......"}`, N, N, N, "(Kèm theo Thông tư số 152/2025/TT-BTC ngày"],
        [`Địa chỉ: ${tkDiaChiKD || tkDiaChi || "......"}`, N, N, N, "31 tháng 12 năm 2025 của Bộ trưởng Bộ Tài"],
        [N, N, N, N, "chính)"],
        Array(5).fill(N),
        [N, N, "SỔ DOANH THU BÁN HÀNG HÓA, DỊCH VỤ", N, N],
        [N, `Địa điểm kinh doanh: ${tkDiaChiKD || ".............................."}`, N, N, N],
        [N, `Kỳ kê khai: Năm 2026`, N, N, N],
        [N, N, N, N, "ĐVT: đồng"],
        ["Chứng từ", N, "Diễn giải", "Số tiền", "Số tiền thuế"],
        ["Số hiệu", "Ngày, tháng", N, N, N],
        ["A", "B", "C", "1", ""],
        ...groupRows,
        [N, N, "Tổng số thuế GTGT phải nộp", soVatTotal, N],
        [N, N, "Tổng số thuế TNCN phải nộp", soPitTotal, N],
        Array(5).fill(N),
        [N, N, N, N, "Ngày ... tháng ... năm ..."],
        [N, N, N, N, "NGƯỜI ĐẠI DIỆN HỘ KINH DOANH/"],
        [N, N, N, N, "CÁ NHÂN KINH DOANH"],
        [N, N, N, N, "(Ký, họ tên, đóng dấu)"],
      ];
      colWidths = [12, 15, 35, 18, 18];
    }
    else if (soType === "s2b") {
      const colCount = 6;
      dataRows = [
        ...xlsxHeaderRows(title, mau, businessInfo, "Năm 2026", colCount),
        [`Ngành nghề: ${ind2?.label || ""} — Tỷ lệ GTGT: ${vatP}%`, null, null, null, null, null],
        Array(colCount).fill(null),
        ["Ngày tháng", "Số HĐ", "Diễn giải", "Doanh thu (đồng)", "Tỷ lệ %", "Số tiền GTGT"],
        ["A", "B", "C", "1", "2", "3"],
        ...sorted.map(e => [fD(e.date), e.note || "", e.desc, e.amount || 0, `${vatP}%`, Math.round(e.amount * (ind2?.vatRate || 0))]),
        ["", "", "Cộng", soTotal, "", Math.round(soTotal * (ind2?.vatRate || 0))],
        ...xlsxFooterRows(colCount),
      ];
      colWidths = [12, 10, 35, 18, 10, 18];
    }
    else if (soType === "s2c") {
      const pitRate2 = BIZ_INDUSTRIES.find(x => x.id === bizIndustry)?.pitRate || 0.015;
      const colCount = 4;
      dataRows = [
        ...xlsxHeaderRows(title, mau, businessInfo, "Năm 2026", colCount),
        ["Số hiệu", "Ngày, tháng", "Diễn giải", "Số tiền"],
        ["A", "B", "C", "1"],
        [s2cData.ctNo || "", fD(s2cData.date || ""), "1. Doanh thu bán hàng hóa, dịch vụ", s2cData.revenue],
        ["", "", "2. Chi phí hợp lý", s2cTotalCost],
        ["", "", "  a) Chi phí NVL, nhiên liệu, năng lượng, hàng hóa", s2cData.cpA],
        ["", "", "  b) Chi phí tiền lương, phụ cấp, BHXH", s2cData.cpB],
        ["", "", "  c) Chi phí khấu hao TSCĐ", s2cData.cpC],
        ["", "", "  d) Chi phí dịch vụ mua ngoài", s2cData.cpD],
        ["", "", "  e) Các khoản chi khác", s2cData.cpE],
        ["", "", "3. Chênh lệch {(3) = (1) − (2)}", s2cProfit],
        ["", "", `4. Tổng số thuế TNCN phải nộp {(4) = (3) × ${pitRate2 * 100}%}`, Math.round(s2cProfit * pitRate2)],
        ...xlsxFooterRows(colCount),
      ];
      colWidths = [12, 15, 45, 20];
    }
    else if (soType === "s2d") {
      const colCount = 8;
      dataRows = [
        ...xlsxHeaderRows(title, mau, businessInfo, "Năm 2026", colCount),
        ["Ngày tháng", "Số CT", "Diễn giải", "Đơn giá", "Nhập (SL)", "Nhập (Thành tiền)", "Xuất (SL)", "Xuất (Thành tiền)"],
        ["A", "B", "C", "1", "2", "3", "4", "5"],
        ...sorted.map(e => [fD(e.date), e.note || "", e.desc, e.amount || 0, "", "", "", e.cost || 0]),
        ["", "", "Cộng", "", "", soTotal, "", soCostTotal],
        ...xlsxFooterRows(colCount),
      ];
      colWidths = [12, 10, 30, 15, 10, 18, 10, 18];
    }
    else { // s2e
      const colCount = 5;
      const bankRows = [];
      s2eData.banks.forEach(b => {
        const bClose = b.open + b.in - b.out;
        bankRows.push(["", "", "Tiền gửi không kỳ hạn", null, null]);
        bankRows.push(["", "", `Ngân hàng: ${b.name || "_______________"}`, null, null]);
        bankRows.push(["", "", "  Tiền gửi đầu kỳ", b.open, ""]);
        bankRows.push(["", "", "  Tổng gửi vào trong kỳ", b.in, ""]);
        bankRows.push(["", "", "  Tổng rút ra trong kỳ", "", b.out]);
        bankRows.push(["", "", "  Tiền gửi cuối kỳ", bClose, ""]);
      });
      dataRows = [
        ...xlsxHeaderRows(title, mau, businessInfo, "Năm 2026", colCount),
        ["Số hiệu", "Ngày tháng", "Diễn giải", "Thu/Gửi vào", "Chi/Rút ra"],
        ["A", "B", "C", "1", "2"],
        [s2eData.ctNo || "", fD(s2eData.date || ""), "Tiền mặt", null, null],
        ["", "", "  Tiền mặt đầu kỳ", s2eData.cashOpen, ""],
        ["", "", "  Tổng tiền thu vào trong kỳ", s2eData.cashIn, ""],
        ["", "", "  Tổng tiền chi ra trong kỳ", "", s2eData.cashOut],
        ["", "", "  Tiền mặt tồn cuối kỳ", s2eCashClose, ""],
        ...bankRows,
        ...xlsxFooterRows(colCount),
      ];
      colWidths = [12, 15, 35, 18, 18];
    }

    buildAndDownloadXlsx({
      fileName: `So_${soType.toUpperCase()}_HKD_2026.xlsx`,
      sheetName: `${soType.toUpperCase()}-HKD`,
      rows: dataRows,
      colWidths,
    });
  };

  const printSoPdf = () => {
    const ind2 = BIZ_INDUSTRIES.find(x => x.id === bizIndustry);
    const fD = (d) => { if (!d) return ""; const p = d.split("-"); return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : d; };
    const fN = n => n ? n.toLocaleString("vi-VN") : "0";
    const sorted = [...soEntries].reverse();
    const vatP = ((ind2?.vatRate || 0) * 100);
    const pitP = ((ind2?.pitRate || 0) * 100);
    // Reuse body builder from generateSoDataUri but with clean PDF CSS
    const pdfCss = `<style>
*{margin:0;padding:0;box-sizing:border-box}
@page{size:A4 portrait;margin:20mm 15mm}
body{font-family:Arial,sans-serif;font-size:10pt;color:#222;padding:20mm 15mm}
table{border-collapse:collapse;width:100%}
td,th{padding:5px 8px;border:1px solid #aaa;vertical-align:middle;font-size:10pt}
.nb{border:none} .ct{text-align:center} .rt{text-align:right}
.num{text-align:right;font-family:'Courier New',monospace}
.colhdr{background:#f0e8d8;font-weight:bold;text-align:center;font-size:9pt}
.total{background:#fff8e7;font-weight:bold}
.bold{font-weight:bold} .it{font-style:italic} .sm{font-size:8pt;color:#888}
h2{text-align:center;margin:8px 0 4px} .sub{text-align:center;font-style:italic;font-size:9pt;color:#666;margin-bottom:12px}
.info{text-align:center;font-size:9pt;margin:2px 0}
.footer-row{margin-top:20px;display:flex;justify-content:space-around;text-align:center}
.footer-col{display:inline-block;width:45%;text-align:center}
</style>`;
    let tableRows = "";
    if (soType === "s1a") {
      tableRows = `<tr><th class="colhdr" style="width:90px"><b>Ngày tháng</b></th><th class="colhdr"><b>Giao dịch</b></th><th class="colhdr" style="width:150px"><b>Số tiền</b></th></tr>
<tr><td class="colhdr ct"><b>A</b></td><td class="colhdr ct"><b>B</b></td><td class="colhdr ct"><b>1</b></td></tr>
${sorted.map(e => `<tr><td class="ct">${fD(e.date)}</td><td>${e.desc}</td><td class="num">${fN(e.amount)}</td></tr>`).join("")}
<tr class="total"><td></td><td class="ct bold">Tổng cộng</td><td class="num">${fN(soTotal)}</td></tr>`;
    } else if (soType === "s2a") {
      const s2aPdfGroups = BIZ_INDUSTRIES.map((ind, gi) => {
        const items = sorted.filter(e => (e.industry || bizIndustry) === ind.id);
        if (items.length === 0) return "";
        const gTotal = items.reduce((s,e) => s+(e.amount||0), 0);
        const gVat = items.reduce((s,e) => s+(e.vatAmount||0), 0);
        const gPit = items.reduce((s,e) => s+(e.pitAmount||0), 0);
        return `<tr><td colspan="4" style="font-weight:bold;background:#fffdf7;padding:6px 8px">${gi+1}. Ngành nghề: ${ind.label} (GTGT ${ind.vatRate*100}% / TNCN ${ind.pitRate*100}%)</td></tr>
${items.map(e => `<tr><td class="ct">${e.note||""}</td><td class="ct">${fD(e.date)}</td><td>${e.desc}</td><td class="num">${fN(e.amount)}</td></tr>`).join("")}
<tr class="total"><td></td><td></td><td class="rt bold">Tổng cộng (${gi+1})</td><td class="num">${fN(gTotal)}</td></tr>
<tr><td></td><td></td><td class="rt" style="font-style:italic">Thuế GTGT (${ind.vatRate*100}%)</td><td class="num" style="color:#1565C0">${fN(gVat)}</td></tr>
<tr><td></td><td></td><td class="rt" style="font-style:italic">Thuế TNCN (${ind.pitRate*100}%)</td><td class="num" style="color:#B8860B">${fN(gPit)}</td></tr>`;
      }).filter(Boolean).join("");
      tableRows = `<tr><th class="colhdr" colspan="2"><b>Chứng từ</b></th><th class="colhdr" rowspan="2"><b>Diễn giải</b></th><th class="colhdr" rowspan="2" style="width:150px"><b>Số tiền</b></th></tr>
<tr><th class="colhdr" style="width:70px"><b>Số hiệu</b></th><th class="colhdr" style="width:80px"><b>Ngày, tháng</b></th></tr>
<tr><td class="colhdr ct"><i>A</i></td><td class="colhdr ct"><i>B</i></td><td class="colhdr ct"><i>C</i></td><td class="colhdr ct"><i>1</i></td></tr>
${s2aPdfGroups}
<tr class="total" style="background:#e3f2fd"><td colspan="3" class="rt bold">Tổng số thuế GTGT phải nộp</td><td class="num bold" style="color:#1565C0">${fN(soVatTotal)}</td></tr>
<tr class="total" style="background:#fff8e1"><td colspan="3" class="rt bold">Tổng số thuế TNCN phải nộp</td><td class="num bold" style="color:#B8860B">${fN(soPitTotal)}</td></tr>`;
    } else if (soType === "s2c") {
      const pitRate2 = BIZ_INDUSTRIES.find(x=>x.id===bizIndustry)?.pitRate||0.015;
      tableRows = `<tr><th class="colhdr" colspan="2"><b>Chứng từ</b></th><th class="colhdr" rowspan="2"><b>Diễn giải</b></th><th class="colhdr" rowspan="2" style="width:150px"><b>Số tiền</b></th></tr>
<tr><th class="colhdr" style="width:70px"><b>Số hiệu</b></th><th class="colhdr" style="width:80px"><b>Ngày, tháng</b></th></tr>
<tr><td class="colhdr ct"><i>A</i></td><td class="colhdr ct"><i>B</i></td><td class="colhdr ct"><i>C</i></td><td class="colhdr ct"><i>1</i></td></tr>
<tr><td class="ct">${s2cData.ctNo||""}</td><td class="ct">${fD(s2cData.date||"")}</td><td class="bold">1. Doanh thu bán hàng hóa, dịch vụ</td><td class="num bold" style="color:#2E7D32">${fN(s2cData.revenue)}</td></tr>
<tr><td></td><td></td><td class="bold" style="color:#C62828">2. Chi phí hợp lý</td><td class="num bold" style="color:#C62828">${fN(s2cTotalCost)}</td></tr>
<tr><td></td><td></td><td style="padding-left:20px">a) NVL, nhiên liệu, năng lượng, hàng hóa</td><td class="num">${fN(s2cData.cpA)}</td></tr>
<tr><td></td><td></td><td style="padding-left:20px">b) Tiền lương, phụ cấp, BHXH</td><td class="num">${fN(s2cData.cpB)}</td></tr>
<tr><td></td><td></td><td style="padding-left:20px">c) Khấu hao TSCĐ</td><td class="num">${fN(s2cData.cpC)}</td></tr>
<tr><td></td><td></td><td style="padding-left:20px">d) Dịch vụ mua ngoài</td><td class="num">${fN(s2cData.cpD)}</td></tr>
<tr><td></td><td></td><td style="padding-left:20px">e) Các khoản chi khác</td><td class="num">${fN(s2cData.cpE)}</td></tr>
<tr class="total"><td></td><td></td><td class="bold">3. Chênh lệch {(3) = (1) − (2)}</td><td class="num bold" style="color:#2E7D32">${fN(s2cProfit)}</td></tr>
<tr class="total" style="background:#fff8e1"><td></td><td></td><td class="bold">4. Thuế TNCN phải nộp {(4) = (3) × ${pitRate2*100}%}</td><td class="num bold" style="color:#B8860B">${fN(Math.round(s2cProfit * pitRate2))}</td></tr>`;
    } else if (soType === "s2e") {
      const bankPdfRows = s2eData.banks.map(b => {
        const bC = b.open + b.in - b.out;
        return `<tr><td></td><td></td><td class="bold" style="color:#1565C0">Tiền gửi không kỳ hạn</td><td></td><td></td></tr>
<tr><td></td><td></td><td class="bold" style="padding-left:10px;font-style:italic">Ngân hàng: ${b.name || "___"}</td><td></td><td></td></tr>
<tr><td></td><td></td><td style="padding-left:20px">Tiền gửi đầu kỳ</td><td class="num">${fN(b.open)}</td><td></td></tr>
<tr><td></td><td></td><td style="padding-left:20px">Tổng gửi vào trong kỳ</td><td class="num">${fN(b.in)}</td><td></td></tr>
<tr><td></td><td></td><td style="padding-left:20px">Tổng rút ra trong kỳ</td><td></td><td class="num">${fN(b.out)}</td></tr>
<tr class="total"><td></td><td></td><td style="padding-left:20px" class="bold">Tiền gửi cuối kỳ</td><td class="num bold" colspan="2">${fN(bC)}</td></tr>`;
      }).join("");
      tableRows = `<tr><th class="colhdr" colspan="2"><b>Chứng từ</b></th><th class="colhdr" rowspan="2"><b>Diễn giải</b></th><th class="colhdr" rowspan="2" style="width:120px"><b>Thu/Gửi vào</b></th><th class="colhdr" rowspan="2" style="width:120px"><b>Chi/Rút ra</b></th></tr>
<tr><th class="colhdr" style="width:70px"><b>Số hiệu</b></th><th class="colhdr" style="width:80px"><b>Ngày tháng</b></th></tr>
<tr><td class="colhdr ct"><i>A</i></td><td class="colhdr ct"><i>B</i></td><td class="colhdr ct"><i>C</i></td><td class="colhdr ct"><i>1</i></td><td class="colhdr ct"><i>2</i></td></tr>
<tr><td class="ct">${s2eData.ctNo||""}</td><td class="ct">${fD(s2eData.date||"")}</td><td class="bold" style="color:#B8860B">Tiền mặt</td><td></td><td></td></tr>
<tr><td></td><td></td><td style="padding-left:20px">Tiền mặt đầu kỳ</td><td class="num">${fN(s2eData.cashOpen)}</td><td></td></tr>
<tr><td></td><td></td><td style="padding-left:20px">Tổng tiền thu vào trong kỳ</td><td class="num">${fN(s2eData.cashIn)}</td><td></td></tr>
<tr><td></td><td></td><td style="padding-left:20px">Tổng tiền chi ra trong kỳ</td><td></td><td class="num">${fN(s2eData.cashOut)}</td></tr>
<tr class="total"><td></td><td></td><td style="padding-left:20px" class="bold">Tiền mặt tồn cuối kỳ</td><td class="num bold" colspan="2">${fN(s2eCashClose)}</td></tr>
${bankPdfRows}`;
    } else {
      tableRows = `<tr><th class="colhdr" style="width:65px">Ngày<br/><span class="sm">A</span></th><th class="colhdr" style="width:45px">Số HĐ<br/><span class="sm">B</span></th><th class="colhdr">Diễn giải<br/><span class="sm">C</span></th><th class="colhdr" style="width:110px">Doanh thu<br/><span class="sm">1</span></th><th class="colhdr" style="width:110px">Chi phí<br/><span class="sm">2</span></th><th class="colhdr" style="width:110px">Lợi nhuận<br/><span class="sm">3</span></th></tr>
${sorted.map(e => `<tr><td class="ct">${fD(e.date)}</td><td class="ct">${e.note||""}</td><td>${e.desc}</td><td class="num">${fN(e.amount)}</td><td class="num">${fN(e.cost)}</td><td class="num">${fN(e.amount-e.cost)}</td></tr>`).join("")}
<tr class="total"><td colspan="3" class="ct bold">Cộng</td><td class="num">${fN(soTotal)}</td><td class="num">${fN(soCostTotal)}</td><td class="num">${fN(soTotal-soCostTotal)}</td></tr>`;
    }
    const soTitles = { s1a:"SỔ CHI TIẾT DOANH THU BÁN HÀNG HÓA, DỊCH VỤ", s2a:"SỔ DOANH THU BÁN HÀNG HÓA, DỊCH VỤ", s2b:"SỔ DOANH THU BÁN HÀNG HÓA, DỊCH VỤ", s2c:"SỔ CHI TIẾT DOANH THU, CHI PHÍ", s2d:"SỔ CHI TIẾT VẬT LIỆU, DỤNG CỤ, HÀNG HÓA", s2e:"SỔ CHI TIẾT TIỀN" };
    const soMau = { s1a:"S1a-HKD", s2a:"S2a-HKD", s2b:"S2b-HKD", s2c:"S2c-HKD", s2d:"S2d-HKD", s2e:"S2e-HKD" };
    const pdfHtml = `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>${soMau[soType]}</title>${pdfCss}</head><body>
<div style="display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:12px">
<div style="font-size:9pt"><b>HỘ, CÁ NHÂN KINH DOANH: ${tkHoTen || "................"}</b><br/>Mã số thuế: ${tkMST || "................"}<br/>Địa chỉ: ${tkDiaChiKD || "................................"}</div>
<div style="font-size:9pt;text-align:right"><b>Mẫu số ${soMau[soType]}</b><br/><i>(Kèm theo Thông tư số 152/2025/TT-BTC<br/>ngày 31 tháng 12 năm 2025 của Bộ trưởng Bộ<br/>Tài chính)</i></div>
</div>
<h2>${soTitles[soType]}</h2>
<div style="font-size:9pt;margin-bottom:4px">Địa điểm kinh doanh: ${tkDiaChiKD || "................................"}</div>
<div style="font-size:9pt;margin-bottom:8px">Kỳ kê khai: Năm 2026</div>
<div style="text-align:right;font-style:italic;font-size:9pt;margin-bottom:6px">Đơn vị tính: đồng</div>
<table>${tableRows}</table>
<div style="margin-top:30px;text-align:right">
<div style="display:inline-block;text-align:center;width:220px">
<div style="font-style:italic;font-size:9pt">Ngày ... tháng ... năm ...</div>
<div style="font-weight:bold;margin-top:4px">NGƯỜI ĐẠI DIỆN HỘ KINH DOANH/<br/>CÁ NHÂN KINH DOANH</div>
<div style="font-style:italic;font-size:8pt">(Ký, họ tên, đóng dấu)</div>
</div>
</div>
<script>window.onload=function(){window.print()}<\/script>
</body></html>`;
    const w = window.open("", "_blank");
    if (w) { w.document.write(pdfHtml); w.document.close(); }
  };
  const [tkMST, setTkMST] = useState("");
  const [tkHoTen, setTkHoTen] = useState("");
  const [tkDiaChi, setTkDiaChi] = useState("");
  const [tkNganhNghe, setTkNganhNghe] = useState("trade");
  const [tkKyThue, setTkKyThue] = useState("q1");
  const [tkDoanhthu, setTkDoanhthu] = useState([0, 0, 0]);
  const [tkDienThoai, setTkDienThoai] = useState("");
  const [tkEmail, setTkEmail] = useState("");
  const [tkSTK, setTkSTK] = useState("");
  const [tkNganHang, setTkNganHang] = useState("");
  const [tkDiaChiKD, setTkDiaChiKD] = useState("");
  const [tkExemptAmount, setTkExemptAmount] = useState(BIZ_TAX_QUARTER_EXEMPT);

  // ====== SALARY CALC (sử dụng module taxCalculator) ======
  const effectiveGross = salaryMode === "net" ? calcGrossFromNetFn({
    targetNet: netInput, dependents, otherDeductions,
    insuranceSalary, useCustomInsurance, allowanceMeal, allowanceOther,
  }) : grossIncome;

  const salaryResult = calcSalaryTax({
    grossIncome: effectiveGross, dependents, otherDeductions,
    insuranceSalary, useCustomInsurance, allowanceMeal, allowanceOther,
    bonus13, contractType,
  });
  const { insurance, mealExempt, totalExempt, incomeBeforeTax, totalDeductions,
    taxableIncome2026, taxableIncome2025, result2026, result2025,
    netIncome, effectiveRate, taxThreshold, minGrossForTax,
    shortContractTax, bonus13Taxable, monthlyAvgWithBonus } = salaryResult;

  // ====== HKD CALC (sử dụng module taxCalculator) ======
  const bizResult = calcBusinessTax({ revenue: bizRevenue, industryId: bizIndustry, method: bizMethod, cost: bizCost });
  const { selectedIndustry, isExempt: bizIsExempt, revenueGroup: bizRevenueGroup,
    effectiveMethod: bizEffectiveMethod, vat: bizVat, pit: bizPit,
    pitLabel: bizPitLabel, totalTax: bizTotalTax } = bizResult;

  // ====== DN CALC (sử dụng module taxCalculator) ======
  const dnResult = calcEnterpriseTax({
    revenue: dnRevenue, cost: dnCost, otherIncome: dnOtherIncome,
    exemptIncome: dnExemptIncome, lossCarry: dnLossCarry,
    vatMethod: dnVatMethod, vatOut: dnVatOut, vatIn: dnVatIn,
  });
  const { taxableIncome: dnTaxableIncome, taxRate: dnTaxRate,
    tndn: dnTndn, vat: dnVat, profit: dnProfit } = dnResult;

  // ====== DEADLINE ======
  const today = new Date();
  const parseDate = (s) => { const [d, m, y] = s.split("/"); return new Date(y, m - 1, d); };
  const nearestDeadline = DEADLINES.filter(d => parseDate(d.date) >= today)[0];
  const daysUntil = nearestDeadline ? Math.ceil((parseDate(nearestDeadline.date) - today) / 86400000) : null;
  const groupLabels = GROUP_LABELS;

  // Tờ khai computations
  const tkData = calcTkData({
    industryId: tkNganhNghe,
    monthlyRevenue: tkDoanhthu,
    exemptAmount: tkExemptAmount,
  });
  const {
    industry: tkIndustry,
    totalRevenue: tkTotalRevenue,
    vat: tkVat,
    taxableRevenue: tkTaxableRevenue,
    pit: tkPit,
    exemptAmount: tkQuarterExempt,
  } = tkData;
  const tkQuarterLabels = QUARTER_LABELS;
  const tkMonthsByQuarter = MONTHS_BY_QUARTER;
  const tkMonths = tkMonthsByQuarter[tkKyThue];
  const tkAnnualEstimate = tkTotalRevenue * 4;
  const tkUsesRevenueMethod = tkAnnualEstimate <= 3_000_000_000;
  const tkApplicableForm = tkAnnualEstimate <= BIZ_TAX_EXEMPT ? "01/TKN-CNKD" : "01/CNKD";

  // ====== AUTO-LOAD TỪ API ======
  const loadConfig = useCallback(async () => {
    try {
      const res = await axios.get(`${END_POINT}/api/tax-accounting/config`, { headers: authHeaders() });
      const c = res.data.data;
      if (c) {
        if (c.mst) setTkMST(c.mst);
        if (c.ho_ten) setTkHoTen(c.ho_ten);
        if (c.dia_chi) setTkDiaChi(c.dia_chi);
        if (c.dia_chi_kd) setTkDiaChiKD(c.dia_chi_kd);
        if (c.dien_thoai) setTkDienThoai(c.dien_thoai);
        if (c.email) setTkEmail(c.email);
        if (c.stk) setTkSTK(c.stk);
        if (c.ngan_hang) setTkNganHang(c.ngan_hang);
        if (c.industry_id) { setBizIndustry(c.industry_id); setTkNganhNghe(c.industry_id); }
      }
    } catch (e) { console.error("loadConfig", e); }
  }, []);

  const loadSoEntries = useCallback(async () => {
    setSoEntriesStorageReady(false);
    try {
      const storedEntries = getStoredSoEntries(soType);
      if (storedEntries && Array.isArray(storedEntries) && soType !== "s2c" && soType !== "s2e") {
        setSoEntries(storedEntries);
        setSoDraft(prev => ({ ...prev, date: getNextSoDraftDate(storedEntries), desc: "Doanh thu bán hàng", amount: 0 }));
        setSoEntriesStorageReady(true);
        return;
      }
      const res = await axios.get(`${END_POINT}/api/tax-accounting/entries?book_type=${soType}`, { headers: authHeaders() });
      const data = res.data.data || [];
      if (soType === "s2c") {
        // S2c: single row structure
        const rev = data.find(e => e.description?.includes("Doanh thu")) || {};
        const costs = data.filter(e => !e.description?.includes("Doanh thu"));
        setS2cData({
          revenue: parseFloat(rev.amount) || 0,
          cpA: parseFloat(costs.find(e => e.note === "cpA")?.amount) || 0,
          cpB: parseFloat(costs.find(e => e.note === "cpB")?.amount) || 0,
          cpC: parseFloat(costs.find(e => e.note === "cpC")?.amount) || 0,
          cpD: parseFloat(costs.find(e => e.note === "cpD")?.amount) || 0,
          cpE: parseFloat(costs.find(e => e.note === "cpE")?.amount) || 0,
          ctNo: rev.note || "", date: rev.date || "",
        });
      } else if (soType === "s2e") {
        // S2e: cash + bank entries
        const cashEntry = data.find(e => e.note === "cash") || {};
        const bankEntries = data.filter(e => e.note?.startsWith("bank_"));
        setS2eData({
          cashOpen: parseFloat(cashEntry.amount) || 0,
          cashIn: parseFloat(cashEntry.vat_amount) || 0,
          cashOut: parseFloat(cashEntry.pit_amount) || 0,
          ctNo: cashEntry.description || "", date: cashEntry.date || "",
          banks: bankEntries.length > 0
            ? bankEntries.map(b => ({
              name: b.description || "", open: parseFloat(b.amount) || 0,
              in: parseFloat(b.vat_amount) || 0, out: parseFloat(b.pit_amount) || 0,
            }))
            : [{ name: "", open: 0, in: 0, out: 0 }],
        });
      } else {
        // S1a, S2a, S2b, S2d: standard entry list
        const loadedEntries = data.map(e => ({
          date: e.date || "", desc: e.description || "", amount: parseFloat(e.amount) || 0,
          cost: parseFloat(e.cost) || 0, vatAmount: parseFloat(e.vat_amount) || 0,
          pitAmount: parseFloat(e.pit_amount) || 0, note: e.note || "",
          industry: e.industry_id || bizIndustry,
        })).reverse();
        setSoEntries(loadedEntries);
        setSoDraft(prev => ({ ...prev, date: getNextSoDraftDate(loadedEntries), desc: "Doanh thu bán hàng", amount: 0 }));
      }
    } catch (e) { console.error("loadSoEntries", e); }
    finally { setSoEntriesStorageReady(true); }
  }, [soType, bizIndustry]);

  const loadTkRevenue = useCallback(async () => {
    try {
      const res = await axios.get(`${END_POINT}/api/tax-accounting/quarter-revenue?year=2026&quarter=${tkKyThue}`, { headers: authHeaders() });
      const data = res.data.data || [];
      setTkDoanhthu(data.map(m => m.revenue || 0));
    } catch (e) { console.error("loadTkRevenue", e); }
  }, [tkKyThue]);

  // Load config on mount
  useEffect(() => { loadConfig(); }, [loadConfig]);

  // Load sổ sách when switching to sosach tab or changing soType
  useEffect(() => {
    if (activeTab === "hkd" && hkdSubTab === "sosach") loadSoEntries();
  }, [activeTab, hkdSubTab, soType, loadSoEntries]);

  // Persist dữ liệu nhập nhanh để F5 không mất dòng đã nhập trên local.
  useEffect(() => {
    if (soType === "s2c" || soType === "s2e" || !soEntriesStorageReady) return;
    storeSoEntries(soType, soEntries);
  }, [soType, soEntries, soEntriesStorageReady]);

  // Load tờ khai revenue when switching to tokhai tab or changing quarter
  useEffect(() => {
    if (activeTab === "hkd" && hkdSubTab === "tokhai") loadTkRevenue();
  }, [activeTab, hkdSubTab, tkKyThue, loadTkRevenue]);

  const downloadTkXlsx = () => {
    const ind = tkIndustry;
    const c = 8;
    const N = null;
    const rows = [
      ["CỘNG HÒA XÃ HỘI CHỦ NGHĨA VIỆT NAM", N, N, N, N, N, N, N],
      ["Độc lập - Tự do - Hạnh phúc", N, N, N, N, N, N, N],
      Array(c).fill(N),
      ["TỜ KHAI THUẾ ĐỐI VỚI HỘ KINH DOANH, CÁ NHÂN KINH DOANH", N, N, N, N, N, N, N],
      ["(Mẫu số 01/CNKD - Ban hành kèm Thông tư 50/2026/TT-BTC)", N, N, N, N, N, N, N],
      [`☑ Nộp thuế TNCN trên ${tkUsesRevenueMethod ? "doanh thu tính thuế" : "thu nhập tính thuế"}`, N, N, N, N, N, N, N],
      Array(c).fill(N),
      [`[01b] Kỳ tính thuế: ${tkQuarterLabels[tkKyThue]} năm 2026`, N, N, N, N, N, N, N],
      ["[02] Lần đầu: ☑    [03] Bổ sung lần thứ: ____", N, N, N, N, N, N, N],
      Array(c).fill(N),
      ["I. THÔNG TIN NGƯỜI NỘP THUẾ", N, N, N, N, N, N, N],
      ["[04] Mã số thuế:", N, tkMST, N, N, N, N, N],
      ["[05] Họ và tên:", N, tkHoTen, N, N, N, N, N],
      ["[06] Địa chỉ trụ sở KD:", N, tkDiaChiKD || tkDiaChi, N, N, N, N, N],
      ["[07] Điện thoại:", N, tkDienThoai, N, N, N, N, N],
      ["[08] Email:", N, tkEmail, N, N, N, N, N],
      Array(c).fill(N),
      ["II. KÊ KHAI THUẾ GTGT, TNCN", N, N, N, N, N, N, N],
      ["[09] Nhóm ngành nghề", "[10] Mã chỉ tiêu", "[11] Tổng doanh thu", "[12] DT không chịu GTGT", "[13] DT thuế suất 0%", "[14] Thuế GTGT phải nộp", "[15] DT chịu thuế TNCN", "[16] Doanh thu được trừ"],
      [ind.label, ind.id, tkTotalRevenue, 0, 0, tkVat, tkTaxableRevenue, Math.round(tkQuarterExempt)],
      ["[17] Thuế TNCN phải nộp", tkPit, N, N, N, N, N, N],
      Array(c).fill(N),
      ["III. THÔNG TIN TÀI KHOẢN NGÂN HÀNG", N, N, N, N, N, N, N],
      ["Số tài khoản:", N, tkSTK, N, N, N, N, N],
      ["Tại ngân hàng:", N, tkNganHang, N, N, N, N, N],
      Array(c).fill(N),
      ["Tôi cam đoan số liệu khai trên là đúng và chịu trách nhiệm trước pháp luật về số liệu đã khai.", N, N, N, N, N, N, N],
      Array(c).fill(N),
      [N, N, N, N, N, "Ngày __ tháng __ năm 2026", N, N],
      [N, N, N, N, N, "NGƯỜI NỘP THUẾ", N, N],
      [N, N, N, N, N, "(Ký, ghi rõ họ tên)", N, N],
    ];
    buildAndDownloadXlsx({
      fileName: `ToKhai_01CNKD_${tkKyThue.toUpperCase()}_2026.xlsx`,
      sheetName: "01-CNKD",
      rows,
      colWidths: [24, 16, 18, 20, 18, 20, 20, 20],
    });
  };
  const tkCanDownload = !!(tkMST && tkHoTen && tkTotalRevenue > 0 && tkApplicableForm === "01/CNKD");

  const printTkPdf = () => {
    const ind = tkIndustry;
    const fmtN2 = n => n.toLocaleString("vi-VN");
    const pdfHtml = `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>01/CNKD ${tkQuarterLabels[tkKyThue]} 2026</title>
<style>
*{margin:0;padding:0;box-sizing:border-box}
@page{size:A4 portrait;margin:20mm 15mm}
body{font-family:Arial,sans-serif;font-size:11pt;color:#222;padding:20mm 15mm}
table{border-collapse:collapse;width:100%;margin-bottom:16px}
td,th{padding:5px 8px;border:1px solid #aaa;font-size:10pt;vertical-align:middle}
.nb{border:none} .ct{text-align:center} .num{text-align:right;font-family:'Courier New',monospace}
.sec{font-weight:bold;background:#f5f0e0;padding:6px 8px}
.hdr th{background:#3d2e1f;color:#fff;font-weight:bold;font-size:9pt}
h1{text-align:center;font-size:14pt;margin:6px 0}
h3{text-align:center;font-size:11pt;margin:4px 0}
.sub{text-align:center;font-style:italic;font-size:9pt;color:#666;margin-bottom:8px}
</style></head><body>
<h3>CỘNG HÒA XÃ HỘI CHỦ NGHĨA VIỆT NAM</h3>
<div class="sub" style="font-weight:bold">Độc lập - Tự do - Hạnh phúc</div>
<div style="text-align:center;margin:8px 0">───────────</div>
  <h1>TỜ KHAI THUẾ ĐỐI VỚI HỘ KINH DOANH, CÁ NHÂN KINH DOANH</h1>
  <div class="sub">(Mẫu số 01/CNKD — Ban hành kèm Thông tư 50/2026/TT-BTC)</div>
  <p style="margin:8px 0">☑ Hộ kinh doanh, cá nhân kinh doanh thuộc đối tượng nộp thuế TNCN trên ${tkUsesRevenueMethod ? "doanh thu tính thuế" : "thu nhập tính thuế"}</p>
  <p style="margin:8px 0"><b>[01b] Kỳ tính thuế: ${tkQuarterLabels[tkKyThue]} năm 2026</b></p>
<p style="margin:4px 0">[02] Lần đầu: ☑ &nbsp;&nbsp; [03] Bổ sung lần thứ: ____</p>

<table style="margin-top:12px">
<tr><td colspan="6" class="sec">I. THÔNG TIN NGƯỜI NỘP THUẾ</td></tr>
<tr><td colspan="2" class="nb"><b>[04] Mã số thuế:</b></td><td colspan="4" class="nb">${tkMST}</td></tr>
<tr><td colspan="2" class="nb"><b>[05] Họ và tên:</b></td><td colspan="4" class="nb">${tkHoTen}</td></tr>
<tr><td colspan="2" class="nb"><b>[06] Địa chỉ:</b></td><td colspan="4" class="nb">${tkDiaChiKD || tkDiaChi}</td></tr>
<tr><td colspan="2" class="nb"><b>[07] Điện thoại:</b></td><td colspan="4" class="nb">${tkDienThoai}</td></tr>
<tr><td colspan="2" class="nb"><b>[08] Email:</b></td><td colspan="4" class="nb">${tkEmail}</td></tr>
  </table>

  <table>
  <tr><td colspan="8" class="sec">II. KÊ KHAI THUẾ GTGT, TNCN</td></tr>
  <tr class="hdr"><th>[09] Nhóm ngành nghề</th><th>[10] Mã</th><th>[11] Tổng DT</th><th>[12] DT không chịu GTGT</th><th>[13] DT thuế suất 0%</th><th>[14] Thuế GTGT</th><th>[15] DT chịu TNCN</th><th>[16] DT được trừ</th></tr>
  <tr><td>${ind.label}</td><td class="ct">${ind.id}</td><td class="num">${fmtN2(tkTotalRevenue)}</td><td class="num">0</td><td class="num">0</td><td class="num">${fmtN2(tkVat)}</td><td class="num">${fmtN2(tkTaxableRevenue)}</td><td class="num">${fmtN2(Math.round(tkQuarterExempt))}</td></tr>
  <tr style="background:#fff8e1"><td colspan="6"><b>[17] Thuế TNCN phải nộp</b></td><td colspan="2" class="num"><b>${fmtN2(tkPit)}</b></td></tr>
  </table>

  <table>
  <tr><td colspan="4" class="sec">III. TÀI KHOẢN NGÂN HÀNG</td></tr>
  <tr><td colspan="2" class="nb"><b>Số tài khoản:</b></td><td colspan="2" class="nb">${tkSTK}</td></tr>
  <tr><td colspan="2" class="nb"><b>Tại ngân hàng:</b></td><td colspan="2" class="nb">${tkNganHang}</td></tr>
</table>

<p style="margin:12px 0;font-size:10pt">Tôi cam đoan số liệu khai trên là đúng và chịu trách nhiệm trước pháp luật về số liệu đã khai.</p>
<div style="text-align:right;margin-top:20px">
<div style="display:inline-block;text-align:center;width:200px">
<div style="font-style:italic;font-size:9pt">Ngày ..... tháng ..... năm 2026</div>
<div style="font-weight:bold;margin-top:4px">NGƯỜI NỘP THUẾ</div>
<div style="font-style:italic;font-size:8pt">(Ký, ghi rõ họ tên)</div>
</div>
</div>
<script>window.onload=function(){window.print()}<\/script>
</body></html>`;
    const w = window.open("", "_blank");
    if (w) { w.document.write(pdfHtml); w.document.close(); }
  };


  return (
    <div style={{ maxWidth: 480, margin: "0 auto", minHeight: "100vh", background: "#FAF6EE", fontFamily: "'Noto Sans', -apple-system, BlinkMacSystemFont, sans-serif", paddingBottom: 80 }}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Mono:wght@400;500&family=Noto+Sans:wght@400;500;600;700&display=swap" rel="stylesheet" />
      <style>{`@keyframes fadeIn { from { opacity:0; transform:translateY(8px); } to { opacity:1; transform:translateY(0); } }
.so-amount::placeholder { font-family: 'Noto Sans', -apple-system, sans-serif; font-weight: 400; font-size: 14px; color: #C4B49A; }`}</style>

      {/* Header */}
      <div style={{ background: "linear-gradient(160deg, #3D2E1F 0%, #5D4230 50%, #8B6914 100%)", padding: "28px 20px 24px", borderRadius: "0 0 28px 28px", position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", top: -30, right: -30, width: 120, height: 120, borderRadius: "50%", background: "rgba(218,165,32,0.12)" }} />
        <div style={{ position: "relative" }}>
          <div style={{ fontSize: 13, color: "rgba(218,165,32,0.9)", fontWeight: 500, letterSpacing: 1.5, textTransform: "uppercase", marginBottom: 4 }}>Thuế TNCN & GTGT 2026</div>
          <div style={{ fontSize: 24, fontWeight: 700, color: "#FFF", lineHeight: 1.2 }}>Hỗ Trợ Người Nộp Thuế</div>
          <div style={{ fontSize: 12, color: "rgba(255,255,255,0.55)", marginTop: 6 }}>Luật Thuế TNCN & GTGT sửa đổi • Biểu thuế 5 bậc & 4 nhóm HKD</div>
        </div>
      </div>

      {/* Tab Bar */}
      <div style={{ display: "flex", gap: 3, padding: "12px 10px 0" }}>
        {tabs.map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)} style={{ flex: 1, padding: "9px 2px", border: "none", borderRadius: 12, background: activeTab === t.id ? "#FFF" : "transparent", color: activeTab === t.id ? "#3D2E1F" : "#A0896C", fontSize: 10, fontWeight: activeTab === t.id ? 600 : 400, cursor: "pointer", display: "flex", flexDirection: "column", alignItems: "center", gap: 2, boxShadow: activeTab === t.id ? "0 2px 8px rgba(0,0,0,0.08)" : "none", transition: "all 0.2s" }}>
            <span style={{ fontSize: 17 }}>{t.icon}</span><span>{t.label}</span>
          </button>
        ))}
      </div>

      <div style={{ padding: "16px 16px 20px" }}>

        {/* ===== NGƯỜI LAO ĐỘNG TAB ===== */}
        {activeTab === "calc" && (<div>
          <div style={{ background: "#FFF", borderRadius: 18, padding: 20, boxShadow: "0 2px 12px rgba(0,0,0,0.04)", marginBottom: 16 }}>
            <div style={{ fontSize: 15, fontWeight: 600, color: "#3D2E1F", marginBottom: 4 }}>Thuế TNCN Người Lao Động</div>
            <div style={{ fontSize: 12, color: "#A0896C", marginBottom: 14 }}>Biểu thuế lũy tiến 5 bậc • Giảm trừ gia cảnh 15.5tr/tháng</div>

            {/* Loại hợp đồng */}
            <label style={{ display: "block", fontSize: 13, color: "#8B7355", marginBottom: 6, fontWeight: 500 }}>Loại hợp đồng lao động</label>
            <div style={{ display: "flex", gap: 6, marginBottom: 14 }}>
              {[{ id: "long", label: "HĐLĐ ≥ 3 tháng", desc: "Thuế lũy tiến" }, { id: "short", label: "HĐLĐ < 3 tháng / TK", desc: "Khấu trừ 10%" }].map(c => (
                <button key={c.id} onClick={() => setContractType(c.id)} style={{ flex: 1, padding: "10px 8px", border: contractType === c.id ? "1.5px solid #B8860B" : "1.5px solid #E8DCC8", borderRadius: 12, background: contractType === c.id ? "#FFF8E7" : "#FFFDF7", cursor: "pointer", textAlign: "center", transition: "all 0.15s" }}>
                  <div style={{ fontSize: 12, fontWeight: contractType === c.id ? 600 : 400, color: contractType === c.id ? "#B8860B" : "#5D4E37" }}>{c.label}</div>
                  <div style={{ fontSize: 10, color: "#A0896C", marginTop: 2 }}>{c.desc}</div>
                </button>
              ))}
            </div>

            {/* Salary mode toggle */}
            <div style={{ display: "flex", gap: 6, marginBottom: 10 }}>
              {[{ id: "gross", label: "Nhập Gross", desc: "Lương trên HĐ" }, { id: "net", label: "Nhập Net", desc: "Lương thực nhận" }].map(m => (
                <button key={m.id} onClick={() => { setSalaryMode(m.id); if (m.id === "gross") setNetInput(0); else setGrossIncome(0); }} style={{ flex: 1, padding: "10px 8px", border: salaryMode === m.id ? "1.5px solid #B8860B" : "1.5px solid #E8DCC8", borderRadius: 12, background: salaryMode === m.id ? "#FFF8E7" : "#FFFDF7", cursor: "pointer", textAlign: "center", transition: "all 0.15s" }}>
                  <div style={{ fontSize: 12, fontWeight: salaryMode === m.id ? 600 : 400, color: salaryMode === m.id ? "#B8860B" : "#5D4E37" }}>{m.label}</div>
                  <div style={{ fontSize: 10, color: "#A0896C", marginTop: 2 }}>{m.desc}</div>
                </button>
              ))}
            </div>

            {salaryMode === "gross" ? (
              <MoneyInput label="Tổng thu nhập (Gross)/tháng" value={grossIncome} onChange={setGrossIncome} placeholder="VD: 30.000.000" />
            ) : (
              <div>
                <MoneyInput label="Lương thực nhận (Net)/tháng" value={netInput} onChange={setNetInput} placeholder="VD: 25.000.000" />
                {netInput > 0 && effectiveGross > 0 && (
                  <div style={{ fontSize: 11, color: "#2E7D32", marginTop: -6, marginBottom: 10, padding: "4px 8px", background: "#E8F5E9", borderRadius: 6 }}>
                    → Lương Gross tương ứng: <b style={{ fontFamily: "'DM Mono', monospace" }}>{fmt(effectiveGross)}</b>/tháng
                  </div>
                )}
              </div>
            )}

            {/* Người phụ thuộc */}
            {contractType === "long" && (<>
              <div style={{ marginBottom: 12 }}>
                <label style={{ display: "block", fontSize: 13, color: "#8B7355", marginBottom: 4, fontWeight: 500 }}>Số người phụ thuộc (đã đăng ký MST)</label>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <button onClick={() => setDependents(Math.max(0, dependents - 1))} style={{ width: 36, height: 36, border: "1.5px solid #D4C4A8", borderRadius: 8, background: "#FFFDF7", fontSize: 18, cursor: "pointer", color: "#8B7355" }}>−</button>
                  <span style={{ fontSize: 20, fontWeight: 700, color: "#3D2E1F", width: 30, textAlign: "center", fontFamily: "'DM Mono', monospace" }}>{dependents}</span>
                  <button onClick={() => setDependents(dependents + 1)} style={{ width: 36, height: 36, border: "1.5px solid #D4C4A8", borderRadius: 8, background: "#FFFDF7", fontSize: 18, cursor: "pointer", color: "#8B7355" }}>+</button>
                </div>
                {dependents > 0 && <div style={{ fontSize: 11, color: "#A0896C", marginTop: 4 }}>Giảm trừ: {fmt(dependents * DEDUCTION_2026.dependent)}/tháng ({dependents} × 6.2tr)</div>}
              </div>

              {/* Nút mở rộng */}
              <button onClick={() => setShowAdvanced(!showAdvanced)} style={{ width: "100%", padding: "10px", border: "1px dashed #D4C4A8", borderRadius: 10, background: "transparent", cursor: "pointer", fontSize: 13, color: "#8B7355", marginBottom: showAdvanced ? 12 : 0, display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
                {showAdvanced ? "▲ Thu gọn" : "▼ Phụ cấp, thưởng & chi tiết"} 
              </button>

              {showAdvanced && (<div style={{ animation: "fadeIn 0.2s ease" }}>
                <MoneyInput label="Phụ cấp ăn trưa/tháng (miễn thuế ≤ 730k)" value={allowanceMeal} onChange={setAllowanceMeal} placeholder="VD: 730.000" />
                <MoneyInput label="Phụ cấp khác miễn thuế (ĐT, công tác phí, đồng phục...)" value={allowanceOther} onChange={setAllowanceOther} placeholder="0" />
                <MoneyInput label="Thưởng tháng 13 / thưởng năm (dự kiến)" value={bonus13} onChange={setBonus13} placeholder="0" />
                <MoneyInput label="Giảm trừ khác (từ thiện, hưu trí TN, y tế, giáo dục...)" value={otherDeductions} onChange={setOtherDeductions} placeholder="0" />
                <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", fontSize: 13, color: "#8B7355", marginTop: 4 }}>
                  <input type="checkbox" checked={useCustomInsurance} onChange={(e) => setUseCustomInsurance(e.target.checked)} style={{ accentColor: "#B8860B" }} />Lương đóng BH khác lương Gross
                </label>
                {useCustomInsurance && <div style={{ marginTop: 8 }}><MoneyInput label="Lương đóng bảo hiểm" value={insuranceSalary} onChange={setInsuranceSalary} /></div>}
              </div>)}
            </>)}
          </div>

          {/* Results */}
          {effectiveGross > 0 && (<div style={{ animation: "fadeIn 0.3s ease" }}>

            {/* Short contract: flat 10% */}
            {contractType === "short" ? (
              <div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 8 }}>
                  <ResultCard label="Khấu trừ 10%/tháng" value={fmt(shortContractTax)} accent />
                  <ResultCard label="Thực nhận (Net)" value={fmt(effectiveGross - shortContractTax)} sub="Không trừ BHXH, giảm trừ" />
                </div>
                <div style={{ background: "#FFF8E7", borderRadius: 14, padding: 14, border: "1px solid #E8DCC8", marginBottom: 12 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: "#8B4513", marginBottom: 6 }}>💡 Lưu ý HĐLĐ dưới 3 tháng / Thời vụ</div>
                  <div style={{ fontSize: 12, color: "#5D4E37", lineHeight: 1.6 }}>
                    • Khấu trừ thuế 10% trên tổng thu nhập mỗi lần chi trả từ 2 triệu đồng trở lên
                    <br/>• Không được giảm trừ gia cảnh khi khấu trừ tại nguồn
                    <br/>• Cuối năm có thể quyết toán thuế để được hoàn nếu thu nhập năm chưa đến mức chịu thuế
                    <br/>• Nếu có MST và cam kết thu nhập năm ≤ {fmtM(DEDUCTION_2026.self * 12)} thì được miễn khấu trừ (Mẫu 08/CK-TNCN)
                  </div>
                </div>
              </div>
            ) : (<>
              {/* Long contract: progressive tax */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 8 }}>
                <ResultCard label="Thuế TNCN/tháng" value={fmt(result2026.total)} accent />
                <ResultCard label="Thực nhận (Net)" value={fmt(netIncome)} sub={`Thuế suất hiệu dụng: ${effectiveRate}%`} />
              </div>

              {/* Ngưỡng chịu thuế */}
              {result2026.total === 0 && effectiveGross > 0 && (
                <div style={{ background: "#E8F5E9", borderRadius: 12, padding: 12, border: "1.5px solid #A5D6A7", marginBottom: 12, textAlign: "center" }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: "#2E7D32" }}>✅ Chưa phải nộp thuế TNCN</div>
                  <div style={{ fontSize: 12, color: "#388E3C", marginTop: 4 }}>Thu nhập tính thuế = 0 sau khi trừ BH & giảm trừ gia cảnh</div>
                </div>
              )}

              {/* Chi tiết bảng lương */}
              <div style={{ background: "#FFF", borderRadius: 16, padding: 16, boxShadow: "0 2px 12px rgba(0,0,0,0.04)", marginBottom: 12 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: "#3D2E1F", marginBottom: 10 }}>Bảng tính lương & thuế chi tiết</div>
                
                {/* Thu nhập */}
                <div style={{ fontSize: 11, color: "#B8860B", fontWeight: 600, marginBottom: 4, textTransform: "uppercase", letterSpacing: 0.5 }}>Thu nhập</div>
                {[
                  { label: "Tổng Gross", val: effectiveGross, bold: true },
                  ...(totalExempt > 0 ? [
                    ...(mealExempt > 0 ? [{ label: "  Phụ cấp ăn trưa (miễn thuế)", val: mealExempt, sub: true }] : []),
                    ...(allowanceOther > 0 ? [{ label: "  Phụ cấp khác miễn thuế", val: allowanceOther, sub: true }] : []),
                    { label: "Thu nhập chịu thuế", val: incomeBeforeTax, bold: true },
                  ] : []),
                ].map((item, i) => (
                  <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "5px 0", borderBottom: "1px solid #F5F0E5" }}>
                    <span style={{ fontSize: 12, color: item.sub ? "#A0896C" : item.bold ? "#3D2E1F" : "#5D4E37", fontWeight: item.bold ? 600 : 400 }}>{item.label}</span>
                    <span style={{ fontSize: 12, fontFamily: "'DM Mono', monospace", color: item.sub ? "#CD853F" : "#3D2E1F" }}>{item.sub ? "−" : ""}{fmt(item.val)}</span>
                  </div>
                ))}

                {/* Các khoản trừ */}
                <div style={{ fontSize: 11, color: "#B8860B", fontWeight: 600, marginTop: 10, marginBottom: 4, textTransform: "uppercase", letterSpacing: 0.5 }}>Các khoản giảm trừ</div>
                {[
                  { label: "BHXH 8% + BHYT 1.5% + BHTN 1%", val: insurance },
                  { label: `Giảm trừ bản thân (15.5tr/tháng)`, val: DEDUCTION_2026.self },
                  ...(dependents > 0 ? [{ label: `Giảm trừ ${dependents} người phụ thuộc (${dependents}×6.2tr)`, val: dependents * DEDUCTION_2026.dependent }] : []),
                  ...(otherDeductions > 0 ? [{ label: "Giảm trừ khác", val: otherDeductions }] : []),
                ].map((item, i) => (
                  <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "5px 0", borderBottom: "1px solid #F5F0E5" }}>
                    <span style={{ fontSize: 12, color: "#8B7355" }}>{item.label}</span>
                    <span style={{ fontSize: 12, fontFamily: "'DM Mono', monospace", color: "#CD853F" }}>−{fmt(item.val)}</span>
                  </div>
                ))}
                <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0 4px", fontWeight: 600 }}>
                  <span style={{ fontSize: 13, color: "#3D2E1F" }}>Thu nhập tính thuế</span>
                  <span style={{ fontSize: 14, fontFamily: "'DM Mono', monospace", color: "#B8860B" }}>{fmt(taxableIncome2026)}</span>
                </div>

                <BracketBar breakdown={result2026.breakdown} total={result2026.total} />

                {/* Kết quả cuối */}
                <div style={{ fontSize: 11, color: "#B8860B", fontWeight: 600, marginTop: 12, marginBottom: 4, textTransform: "uppercase", letterSpacing: 0.5 }}>Kết quả</div>
                {[
                  { label: "Thuế TNCN phải nộp", val: result2026.total, color: "#CD4500" },
                  { label: "Bảo hiểm bắt buộc NLĐ", val: insurance, color: "#1565C0" },
                  { label: "THỰC NHẬN (Net)", val: netIncome, bold: true, color: "#2E7D32" },
                ].map((item, i) => (
                  <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: i < 2 ? "1px solid #F5F0E5" : "none" }}>
                    <span style={{ fontSize: item.bold ? 13 : 12, color: "#3D2E1F", fontWeight: item.bold ? 700 : 400 }}>{item.label}</span>
                    <span style={{ fontSize: item.bold ? 15 : 13, fontFamily: "'DM Mono', monospace", color: item.color, fontWeight: 600 }}>{fmt(item.val)}</span>
                  </div>
                ))}
              </div>

              {/* Ước tính cả năm */}
              <div style={{ background: "linear-gradient(135deg, #3D2E1F, #5D4230)", borderRadius: 16, padding: 16, marginBottom: 12 }}>
                <div style={{ fontSize: 12, color: "#DAA520", fontWeight: 600, marginBottom: 10 }}>📊 Ước tính cả năm 2026</div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                  {[
                    { label: "Tổng Gross/năm", val: effectiveGross * 12 + bonus13 },
                    { label: "Thuế TNCN/năm", val: result2026.total * 12 + (bonus13 > 0 ? Math.round(bonus13 * (result2026.total > 0 ? result2026.total / (taxableIncome2026 || 1) : 0)) : 0) },
                    { label: "BH bắt buộc/năm", val: insurance * 12 },
                    { label: "Thực nhận/năm", val: netIncome * 12 + bonus13 - (bonus13 > 0 ? Math.round(bonus13 * 0.1) : 0) },
                  ].map((item, i) => (
                    <div key={i} style={{ padding: "6px 0" }}>
                      <div style={{ fontSize: 10, color: "rgba(255,255,255,0.55)" }}>{item.label}</div>
                      <div style={{ fontSize: 15, fontWeight: 700, color: i === 3 ? "#4CAF50" : i === 1 ? "#FF8A65" : "#FFF", fontFamily: "'DM Mono', monospace" }}>{fmtM(item.val)}</div>
                    </div>
                  ))}
                </div>
                {bonus13 > 0 && <div style={{ fontSize: 10, color: "rgba(255,255,255,0.4)", marginTop: 6 }}>* Đã bao gồm thưởng tháng 13: {fmtM(bonus13)}</div>}
              </div>

              {/* Ngưỡng thu nhập chịu thuế */}
              <div style={{ background: "#FFF8E7", borderRadius: 14, padding: 14, border: "1px solid #E8DCC8", marginBottom: 12 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: "#8B4513", marginBottom: 6 }}>💡 Ngưỡng chịu thuế TNCN 2026</div>
                <div style={{ fontSize: 12, color: "#5D4E37", lineHeight: 1.7 }}>
                  Với <b>{dependents} người phụ thuộc</b>, bạn bắt đầu đóng thuế khi lương Gross trên khoảng <b style={{ color: "#B8860B" }}>{fmtM(minGrossForTax)}/tháng</b> (sau trừ BH 10.5%)
                </div>
              </div>

              {/* Thông tin quan trọng cho NLĐ */}
              <div style={{ background: "#FFF", borderRadius: 16, padding: 16, boxShadow: "0 2px 12px rgba(0,0,0,0.04)" }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: "#3D2E1F", marginBottom: 10 }}>📋 NLĐ cần biết</div>
                {[
                  { q: "Ai quyết toán thuế?", a: "Ủy quyền DN quyết toán (hạn 31/3) hoặc tự quyết toán (hạn 30/4 năm sau). Nếu chỉ có 1 nguồn thu nhập → nên ủy quyền DN." },
                  { q: "Khi nào được hoàn thuế?", a: "Nếu tổng thuế đã khấu trừ > thuế phải nộp khi quyết toán năm, bạn được hoàn phần chênh lệch." },
                  { q: "Phụ cấp nào miễn thuế?", a: "Ăn trưa ≤ 730k/tháng, đồng phục ≤ 5tr/năm, công tác phí, điện thoại, xăng xe theo quy chế DN." },
                  { q: "Đăng ký người phụ thuộc?", a: "Nộp Mẫu 20-ĐK-TCT qua thuedientu.gdt.gov.vn. Cần CCCD gắn chip + giấy tờ chứng minh quan hệ." },
                ].map((item, i) => (
                  <div key={i} style={{ padding: "8px 0", borderBottom: i < 3 ? "1px solid #F0E8D8" : "none" }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: "#5D4E37" }}>{item.q}</div>
                    <div style={{ fontSize: 11, color: "#8B7355", lineHeight: 1.5, marginTop: 2 }}>{item.a}</div>
                  </div>
                ))}
              </div>
            </>)}
          </div>)}
        </div>)}

        {/* ===== HỘ KINH DOANH TAB ===== */}
        {activeTab === "hkd" && (<div>
          {/* Sub-tab bar */}
          <div style={{ display: "flex", gap: 6, marginBottom: 14 }}>
            {[{ id: "tinhtoan", label: "🧮 Tính thuế", desc: "GTGT & TNCN" }, { id: "sosach", label: "📒 Sổ sách", desc: "TT 152/2025" }, { id: "tokhai", label: "📄 Tờ khai", desc: "01/CNKD" }].map(st => (
              <button key={st.id} onClick={() => setHkdSubTab(st.id)} style={{
                flex: 1, padding: "12px 8px", border: hkdSubTab === st.id ? "1.5px solid #B8860B" : "1.5px solid #E8DCC8",
                borderRadius: 14, background: hkdSubTab === st.id ? "linear-gradient(135deg, #FFF8E7, #FFFDF7)" : "#FFF",
                cursor: "pointer", textAlign: "center", transition: "all 0.2s",
                boxShadow: hkdSubTab === st.id ? "0 2px 8px rgba(184,134,11,0.15)" : "none",
              }}>
                <div style={{ fontSize: 14, fontWeight: hkdSubTab === st.id ? 700 : 400, color: hkdSubTab === st.id ? "#B8860B" : "#8B7355" }}>{st.label}</div>
                <div style={{ fontSize: 10, color: "#A0896C", marginTop: 2 }}>{st.desc}</div>
              </button>
            ))}
          </div>

          {/* Sub-tab: Tính toán */}
          {hkdSubTab === "tinhtoan" && (<div>
          <div style={{ background: "#FFF", borderRadius: 18, padding: 20, boxShadow: "0 2px 12px rgba(0,0,0,0.04)", marginBottom: 16 }}>
            <div style={{ fontSize: 15, fontWeight: 600, color: "#3D2E1F", marginBottom: 4 }}>Thuế GTGT & TNCN Hộ Kinh Doanh</div>
            <div style={{ fontSize: 12, color: "#A0896C", marginBottom: 16 }}>Từ 01/01/2026 • Bỏ thuế khoán • Ngưỡng miễn 1 tỷ/năm</div>

            <label style={{ display: "block", fontSize: 13, color: "#8B7355", marginBottom: 6, fontWeight: 500 }}>Ngành nghề kinh doanh</label>
            <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 16 }}>
              {BIZ_INDUSTRIES.map(ind => (
                <button key={ind.id} onClick={() => setBizIndustry(ind.id)} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", border: bizIndustry === ind.id ? "1.5px solid #B8860B" : "1.5px solid #E8DCC8", borderRadius: 12, background: bizIndustry === ind.id ? "#FFF8E7" : "#FFFDF7", cursor: "pointer", textAlign: "left", transition: "all 0.15s" }}>
                  <span style={{ fontSize: 22, flexShrink: 0 }}>{ind.icon}</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 12, fontWeight: bizIndustry === ind.id ? 600 : 400, color: bizIndustry === ind.id ? "#3D2E1F" : "#5D4E37" }}>{ind.label}</div>
                    <div style={{ fontSize: 10, color: "#A0896C", marginTop: 1 }}>GTGT: {ind.vatRate * 100}% • TNCN: {ind.pitRate * 100}%</div>
                  </div>
                  {bizIndustry === ind.id && <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#B8860B", flexShrink: 0 }} />}
                </button>
              ))}
            </div>

            <MoneyInput label="Doanh thu năm (VNĐ)" value={bizRevenue} onChange={setBizRevenue} placeholder="VD: 900.000.000" />

            {bizRevenue > 0 && (
              <div style={{ padding: "8px 12px", borderRadius: 10, background: bizIsExempt ? "#E8F5E9" : "#FFF8E7", border: `1px solid ${bizIsExempt ? "#A5D6A7" : "#E8DCC8"}`, marginBottom: 12, fontSize: 12, color: bizIsExempt ? "#2E7D32" : "#8B4513", fontWeight: 500 }}>
                {bizIsExempt ? "✅ " : "📊 "}{groupLabels[bizRevenueGroup]}
              </div>
            )}

            {!bizIsExempt && bizRevenue > 0 && (<>
              {/* Nhóm 2: cho chọn phương pháp */}
              {bizRevenueGroup === 2 && (<>
                <label style={{ display: "block", fontSize: 13, color: "#8B7355", marginBottom: 6, fontWeight: 500 }}>Phương pháp tính thuế TNCN</label>
                <div style={{ display: "flex", gap: 6, marginBottom: 12 }}>
                  {[{ id: "revenue", label: "% trên Doanh thu", desc: "Không cần chứng minh chi phí" }, { id: "profit", label: "Theo Lợi nhuận (15%)", desc: "Cần hóa đơn chi phí đầu vào" }].map(m => (
                    <button key={m.id} onClick={() => setBizMethod(m.id)} style={{ flex: 1, padding: "10px 8px", border: bizMethod === m.id ? "1.5px solid #B8860B" : "1.5px solid #E8DCC8", borderRadius: 12, background: bizMethod === m.id ? "#FFF8E7" : "#FFFDF7", cursor: "pointer", textAlign: "center", transition: "all 0.15s" }}>
                      <div style={{ fontSize: 12, fontWeight: bizMethod === m.id ? 600 : 400, color: bizMethod === m.id ? "#B8860B" : "#5D4E37" }}>{m.label}</div>
                      <div style={{ fontSize: 10, color: "#A0896C", marginTop: 2 }}>{m.desc}</div>
                    </button>
                  ))}
                </div>
              </>)}

              {/* Nhóm 3-4: bắt buộc theo lợi nhuận */}
              {bizRevenueGroup >= 3 && (
                <div style={{ padding: "8px 12px", borderRadius: 10, background: "#E3F2FD", border: "1px solid #90CAF9", marginBottom: 12, fontSize: 12, color: "#1565C0" }}>
                  📌 DT trên 3 tỷ: bắt buộc tính thuế TNCN theo <b>lợi nhuận</b> (DT − Chi phí) × thuế suất {bizRevenueGroup === 3 ? "17%" : "20%"}
                </div>
              )}

              {/* Input chi phí: hiện khi chọn profit (nhóm 2) hoặc nhóm 3-4 */}
              {bizEffectiveMethod === "profit" && <MoneyInput label="Chi phí đầu vào có hóa đơn (VNĐ/năm)" value={bizCost} onChange={setBizCost} placeholder="VD: 600.000.000" />}
            </>)}
          </div>

          {/* HKD Results */}
          {bizRevenue > 0 && (<div style={{ animation: "fadeIn 0.3s ease" }}>
            {bizIsExempt ? (
              <div style={{ background: "#E8F5E9", borderRadius: 16, padding: 20, textAlign: "center", border: "1.5px solid #A5D6A7" }}>
                <div style={{ fontSize: 40, marginBottom: 8 }}>🎉</div>
                <div style={{ fontSize: 16, fontWeight: 700, color: "#2E7D32", marginBottom: 4 }}>Miễn thuế GTGT & TNCN</div>
                <div style={{ fontSize: 13, color: "#388E3C" }}>Doanh thu ≤ 1 tỷ đồng/năm</div>
                <div style={{ fontSize: 12, color: "#66BB6A", marginTop: 8 }}>Theo Nghị định 141/2026/NĐ-CP, áp dụng từ 01/01/2026</div>
              </div>
            ) : (<>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 8 }}>
                <ResultCard label="Thuế GTGT/năm" value={fmt(bizVat)} accent color="linear-gradient(135deg, #1565C0 0%, #42A5F5 100%)" sub={`${selectedIndustry.vatRate * 100}% × DT`} />
                <ResultCard label="Thuế TNCN/năm" value={fmt(bizPit)} accent sub={bizPitLabel} />
              </div>

              {/* Total dark card */}
              <div style={{ background: "linear-gradient(135deg, #3D2E1F, #5D4230)", borderRadius: 16, padding: 16, marginBottom: 12 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div>
                    <div style={{ fontSize: 11, color: "rgba(218,165,32,0.8)" }}>Tổng thuế phải nộp/năm</div>
                    <div style={{ fontSize: 24, fontWeight: 700, color: "#FFF", fontFamily: "'DM Mono', monospace" }}>{fmt(bizTotalTax)}</div>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontSize: 11, color: "rgba(255,255,255,0.6)" }}>Tỷ lệ thuế/DT</div>
                    <div style={{ fontSize: 18, fontWeight: 700, color: "#DAA520", fontFamily: "'DM Mono', monospace" }}>{(bizTotalTax / bizRevenue * 100).toFixed(2)}%</div>
                  </div>
                </div>
                {bizTotalTax > 0 && (<>
                  <div style={{ marginTop: 12, display: "flex", borderRadius: 8, overflow: "hidden", height: 10 }}>
                    <div style={{ width: `${bizVat / bizTotalTax * 100}%`, background: "#42A5F5", transition: "width 0.3s" }} />
                    <div style={{ width: `${bizPit / bizTotalTax * 100}%`, background: "#DAA520", transition: "width 0.3s" }} />
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", marginTop: 6 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 4 }}><div style={{ width: 8, height: 8, borderRadius: 2, background: "#42A5F5" }} /><span style={{ fontSize: 10, color: "rgba(255,255,255,0.6)" }}>GTGT: {fmt(bizVat)}</span></div>
                    <div style={{ display: "flex", alignItems: "center", gap: 4 }}><div style={{ width: 8, height: 8, borderRadius: 2, background: "#DAA520" }} /><span style={{ fontSize: 10, color: "rgba(255,255,255,0.6)" }}>TNCN: {fmt(bizPit)}</span></div>
                  </div>
                </>)}
              </div>

              {/* Detail breakdown */}
              <div style={{ background: "#FFF", borderRadius: 16, padding: 16, boxShadow: "0 2px 12px rgba(0,0,0,0.04)", marginBottom: 12 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: "#3D2E1F", marginBottom: 10 }}>Chi tiết tính thuế</div>
                {[
                  { label: "Doanh thu năm", val: bizRevenue },
                  { label: "Ngưỡng miễn thuế TNCN", val: BIZ_TAX_EXEMPT, isSub: true },
                  ...(bizEffectiveMethod === "profit" ? [{ label: "Chi phí đầu vào", val: bizCost, isSub: true }] : []),
                  { label: `Thuế GTGT (${selectedIndustry.vatRate * 100}% × DT)`, val: bizVat, color: "#1565C0", isHighlight: true },
                  { label: `Thuế TNCN (${bizPitLabel})`, val: bizPit, color: "#B8860B", isHighlight: true },
                ].map((row, i) => (
                  <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: "1px solid #F0E8D8" }}>
                    <span style={{ fontSize: 12, color: row.isSub ? "#A0896C" : "#5D4E37" }}>{row.label}</span>
                    <span style={{ fontSize: 13, fontFamily: "'DM Mono', monospace", color: row.color || (row.isSub ? "#CD853F" : "#3D2E1F"), fontWeight: row.isHighlight ? 600 : 400 }}>{row.isSub ? "−" : ""}{fmt(row.val)}</span>
                  </div>
                ))}
                <div style={{ display: "flex", justifyContent: "space-between", padding: "10px 0 4px", fontWeight: 700 }}>
                  <span style={{ fontSize: 13, color: "#3D2E1F" }}>Tổng thuế</span>
                  <span style={{ fontSize: 15, fontFamily: "'DM Mono', monospace", color: "#8B4513" }}>{fmt(bizTotalTax)}</span>
                </div>
              </div>

              {/* Monthly estimate */}
              <div style={{ background: "linear-gradient(135deg, #FFFDF7, #FFF8E7)", borderRadius: 14, padding: 14, border: "1px solid #E8DCC8", marginBottom: 12 }}>
                <div style={{ fontSize: 12, color: "#8B7355", fontWeight: 500, marginBottom: 6 }}>Ước tính hàng tháng</div>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <div><div style={{ fontSize: 11, color: "#A0896C" }}>Thuế/tháng</div><div style={{ fontSize: 16, fontWeight: 700, color: "#8B4513", fontFamily: "'DM Mono', monospace" }}>{fmtM(bizTotalTax / 12)}</div></div>
                  <div style={{ textAlign: "center" }}><div style={{ fontSize: 11, color: "#A0896C" }}>GTGT/tháng</div><div style={{ fontSize: 14, fontWeight: 600, color: "#1565C0", fontFamily: "'DM Mono', monospace" }}>{fmtM(bizVat / 12)}</div></div>
                  <div style={{ textAlign: "right" }}><div style={{ fontSize: 11, color: "#A0896C" }}>TNCN/tháng</div><div style={{ fontSize: 14, fontWeight: 600, color: "#B8860B", fontFamily: "'DM Mono', monospace" }}>{fmtM(bizPit / 12)}</div></div>
                </div>
              </div>
            </>)}

            {/* Rate table */}
            <div style={{ background: "linear-gradient(135deg, #3D2E1F, #5D4230)", borderRadius: 16, padding: 16, marginTop: 4 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: "#DAA520", marginBottom: 10 }}>📌 Bảng tỷ lệ thuế theo ngành</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr auto auto", marginBottom: 4 }}>
                <span style={{ fontSize: 10, color: "rgba(255,255,255,0.5)", paddingBottom: 6 }}>Ngành nghề</span>
                <span style={{ fontSize: 10, color: "rgba(66,165,245,0.8)", textAlign: "center", paddingBottom: 6, minWidth: 44 }}>GTGT</span>
                <span style={{ fontSize: 10, color: "rgba(218,165,32,0.8)", textAlign: "center", paddingBottom: 6, minWidth: 44 }}>TNCN</span>
              </div>
              {BIZ_INDUSTRIES.map((ind, i) => (
                <div key={i} style={{ display: "grid", gridTemplateColumns: "1fr auto auto", padding: "6px 0", borderBottom: i < BIZ_INDUSTRIES.length - 1 ? "1px solid rgba(255,255,255,0.08)" : "none", background: ind.id === bizIndustry ? "rgba(218,165,32,0.1)" : "none", borderRadius: 4 }}>
                  <span style={{ fontSize: 11, color: ind.id === bizIndustry ? "#DAA520" : "rgba(255,255,255,0.65)", fontWeight: ind.id === bizIndustry ? 600 : 400 }}>{ind.icon} {ind.label.length > 20 ? ind.label.slice(0, 20) + "…" : ind.label}</span>
                  <span style={{ fontSize: 12, color: "#42A5F5", textAlign: "center", fontFamily: "'DM Mono', monospace", fontWeight: 600, minWidth: 44 }}>{ind.vatRate * 100}%</span>
                  <span style={{ fontSize: 12, color: "#DAA520", textAlign: "center", fontFamily: "'DM Mono', monospace", fontWeight: 600, minWidth: 44 }}>{ind.pitRate * 100}%</span>
                </div>
              ))}
            </div>
          </div>)}
          </div>)}

          {/* Sub-tab: Sổ sách kế toán */}
          {hkdSubTab === "sosach" && (<div>
            <div style={{ background: "#FFF", borderRadius: 18, padding: 20, boxShadow: "0 2px 12px rgba(0,0,0,0.04)", marginBottom: 16 }}>
              <div style={{ fontSize: 15, fontWeight: 600, color: "#3D2E1F", marginBottom: 4 }}>Sổ Kế Toán Hộ Kinh Doanh</div>
              <div style={{ fontSize: 12, color: "#A0896C", marginBottom: 14 }}>Thông tư 152/2025/TT-BTC • Nhập liệu & xuất Excel</div>

              {/* Chọn loại sổ */}
              <label style={{ display: "block", fontSize: 13, color: "#8B7355", marginBottom: 6, fontWeight: 500 }}>Chọn loại sổ</label>
              <div style={{ display: "flex", flexDirection: "column", gap: 5, marginBottom: 16 }}>
                {[
                  { id: "s1a", label: "S1a-HKD", desc: "Sổ DT bán hàng (Nhóm 1, DT ≤ 1 tỷ)", color: "#4CAF50" },
                  { id: "s2a", label: "S2a-HKD", desc: "Sổ DT theo ngành (Nhóm 2, thuế % trên DT)", color: "#B8860B" },
                  { id: "s2b", label: "S2b-HKD", desc: "Sổ DT PP theo thu nhập (Nhóm 2-3)", color: "#E65100" },
                  { id: "s2c", label: "S2c-HKD", desc: "Sổ chi tiết doanh thu, chi phí (Nhóm 3+)", color: "#1565C0" },
                  { id: "s2d", label: "S2d-HKD", desc: "Sổ chi tiết vật liệu, hàng hóa", color: "#6A1B9A" },
                  { id: "s2e", label: "S2e-HKD", desc: "Sổ chi tiết tiền (thu chi)", color: "#00695C" },
                ].map(s => (
                  <button key={s.id} onClick={() => { setSoEntriesStorageReady(false); setSoType(s.id); setSoEntries([]); }} style={{
                    display: "flex", alignItems: "center", gap: 10, padding: "9px 12px",
                    border: soType === s.id ? `1.5px solid ${s.color}` : "1.5px solid #E8DCC8",
                    borderRadius: 10, background: soType === s.id ? "#FFF8E7" : "#FFFDF7",
                    cursor: "pointer", textAlign: "left", transition: "all 0.15s",
                  }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: s.color, fontFamily: "'DM Mono', monospace", minWidth: 62 }}>{s.label}</span>
                    <span style={{ fontSize: 11, color: soType === s.id ? "#3D2E1F" : "#8B7355", fontWeight: soType === s.id ? 500 : 400, flex: 1 }}>{s.desc}</span>
                    {soType === s.id && <div style={{ width: 8, height: 8, borderRadius: "50%", background: s.color, flexShrink: 0 }} />}
                  </button>
                ))}
              </div>

              {/* Tổng cộng - ABOVE entries */}
              {soEntries.length > 0 && (
                <div style={{ background: "linear-gradient(135deg, #3D2E1F, #5D4230)", borderRadius: 14, padding: 14, marginBottom: 12, color: "#FFF" }}>
                  <div style={{ fontSize: 12, color: "#DAA520", fontWeight: 600, marginBottom: 8 }}>Tổng cộng ({soEntries.length} dòng)</div>
                  <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 8, marginBottom: soByIndustry.length > 0 ? 10 : 0 }}>
                    <div><div style={{ fontSize: 10, color: "rgba(255,255,255,0.55)" }}>Tổng DT/Thu</div><div style={{ fontSize: 16, fontWeight: 700, fontFamily: "'DM Mono', monospace" }}>{fmtM(soTotal)}</div></div>
                    {(soType === "s2a") && (<>
                      <div><div style={{ fontSize: 10, color: "rgba(255,255,255,0.55)" }}>Tổng GTGT</div><div style={{ fontSize: 14, fontWeight: 600, color: "#64B5F6", fontFamily: "'DM Mono', monospace" }}>{fmtM(soVatTotal)}</div></div>
                      <div><div style={{ fontSize: 10, color: "rgba(255,255,255,0.55)" }}>Tổng TNCN</div><div style={{ fontSize: 14, fontWeight: 600, color: "#DAA520", fontFamily: "'DM Mono', monospace" }}>{fmtM(soPitTotal)}</div></div>
                    </>)}
                    {(soType === "s2b" || soType === "s2c" || soType === "s2e") && (<>
                      <div><div style={{ fontSize: 10, color: "rgba(255,255,255,0.55)" }}>Tổng Chi phí</div><div style={{ fontSize: 14, fontWeight: 600, color: "#FF8A65", fontFamily: "'DM Mono', monospace" }}>{fmtM(soCostTotal)}</div></div>
                      {soType !== "s2c" && <div><div style={{ fontSize: 10, color: "rgba(255,255,255,0.55)" }}>{soType === "s2e" ? "Tồn quỹ" : "Lợi nhuận"}</div><div style={{ fontSize: 14, fontWeight: 600, color: "#4CAF50", fontFamily: "'DM Mono', monospace" }}>{fmtM(soTotal - soCostTotal)}</div></div>}
                    </>)}
                  </div>
                  {/* S2a: per-industry breakdown */}
                  {soByIndustry.length > 0 && (<>
                    <div style={{ borderTop: "1px solid rgba(255,255,255,0.15)", paddingTop: 8 }}>
                      <div style={{ fontSize: 10, color: "rgba(255,255,255,0.5)", marginBottom: 6 }}>Chi tiết theo ngành nghề:</div>
                      {soByIndustry.map((g, i) => (
                        <div key={i} style={{ padding: "5px 0", borderBottom: i < soByIndustry.length - 1 ? "1px solid rgba(255,255,255,0.08)" : "none" }}>
                          <div style={{ fontSize: 11, color: "#DAA520", fontWeight: 500, marginBottom: 2 }}>{g.icon} {g.label} ({g.count} bút toán)</div>
                          <div style={{ display: "flex", gap: 12, fontSize: 10 }}>
                            <span style={{ color: "rgba(255,255,255,0.7)" }}>DT: <b style={{ fontFamily: "'DM Mono', monospace" }}>{fmtM(g.total)}</b></span>
                            <span style={{ color: "#64B5F6" }}>GTGT: <b style={{ fontFamily: "'DM Mono', monospace" }}>{fmtM(g.vat)}</b></span>
                            <span style={{ color: "#DAA520" }}>TNCN: <b style={{ fontFamily: "'DM Mono', monospace" }}>{fmtM(g.pit)}</b></span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>)}
                </div>
              )}

              {/* Export - single click download */}
              {soEntries.length > 0 ? (
                <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
                  <button onClick={downloadSoXlsx} style={{
                    flex: 1, padding: "14px", borderRadius: 14, border: "none",
                    background: "linear-gradient(135deg, #1565C0, #42A5F5)", color: "#FFF", fontSize: 14,
                    fontWeight: 700, textAlign: "center", boxSizing: "border-box",
                    boxShadow: "0 4px 12px rgba(21,101,192,0.3)", cursor: "pointer",
                  }}>
                    📥 Excel (.xlsx)
                  </button>
                  <button onClick={printSoPdf} style={{
                    flex: 1, padding: "14px", borderRadius: 14, border: "none",
                    background: "linear-gradient(135deg, #C62828, #EF5350)", color: "#FFF", fontSize: 14,
                    fontWeight: 700, textAlign: "center", cursor: "pointer",
                    boxShadow: "0 4px 12px rgba(198,40,40,0.3)",
                  }}>
                    📄 In / Lưu PDF
                  </button>
                </div>
              ) : (
                <div style={{
                  width: "100%", padding: "14px", borderRadius: 14, marginBottom: 12,
                  background: "#D4C4A8", color: "#FFF", fontSize: 15, fontWeight: 700,
                  textAlign: "center", boxSizing: "border-box", cursor: "not-allowed",
                }}>
                  📥 Tải về Excel (chưa có dữ liệu)
                </div>
              )}

              {/* Entries input + monthly table — for S1a, S2a, S2b, S2d only */}
              {soType !== "s2c" && soType !== "s2e" && (<>
              <div style={{ fontSize: 13, fontWeight: 600, color: "#3D2E1F", marginBottom: 8, paddingTop: 8, borderTop: "1px solid #F0E8D8" }}>
                <span>Nhập liệu ({soEntries.length} dòng)</span>
              </div>

              <div style={{ background: "#FFF8E7", borderRadius: 12, padding: 12, marginBottom: 10, border: "1px solid #F0E8D8", boxShadow: "0 2px 8px rgba(184,134,11,0.08)" }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: "#3D2E1F", marginBottom: 10 }}>Thêm nghiệp vụ mới</div>
                <div style={{ marginBottom: 8 }}>
                  <label style={{ display: "block", fontSize: 10, color: "#8B7355", marginBottom: 4 }}>Ngày tháng</label>
                  <input
                    type="date"
                    value={soDraft.date}
                    onInput={e => setSoDraft({ ...soDraft, date: e.target.value })}
                    onChange={e => setSoDraft({ ...soDraft, date: e.target.value })}
                    style={{ display: "block", width: "100%", padding: "9px 10px", border: "1px solid #D4C4A8", borderRadius: 8, fontSize: 14, background: "#FFF", boxSizing: "border-box", color: "#3D2E1F", WebkitAppearance: "none", MozAppearance: "none", appearance: "none", minHeight: 40 }}
                  />
                </div>
                <div style={{ marginBottom: 8 }}>
                  <label style={{ display: "block", fontSize: 10, color: "#8B7355", marginBottom: 4 }}>Diễn giải</label>
                  <input
                    type="text"
                    value={soDraft.desc}
                    readOnly
                    style={{ display: "block", width: "100%", padding: "9px 10px", border: "1px solid #D4C4A8", borderRadius: 8, fontSize: 13, background: "#FFFDF7", boxSizing: "border-box", color: "#3D2E1F", minHeight: 40, fontWeight: 600 }}
                  />
                </div>
                <div style={{ marginBottom: 10 }}>
                  <label style={{ display: "block", fontSize: 10, color: "#8B7355", marginBottom: 4 }}>Số tiền</label>
                  <input
                    ref={newAmountRef}
                    type="text"
                    inputMode="numeric"
                    className="so-amount"
                    value={soDraft.amount ? fmt(soDraft.amount) : ""}
                    onChange={e => setSoDraft({ ...soDraft, amount: parseInt(e.target.value.replace(/\D/g, "")) || 0 })}
                    onKeyDown={e => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        addSoEntry();
                      }
                    }}
                    placeholder="Nhập số tiền..."
                    style={{ display: "block", width: "100%", padding: "10px", border: "1px solid #D4C4A8", borderRadius: 8, fontSize: 16, fontFamily: soDraft.amount ? "'DM Mono', monospace" : "'Noto Sans', sans-serif", fontWeight: soDraft.amount ? 600 : 400, background: "#FFF", boxSizing: "border-box", color: "#3D2E1F", minHeight: 42 }}
                  />
                </div>
                <button
                  type="button"
                  onClick={addSoEntry}
                  disabled={!soDraft.date || !soDraft.amount}
                  style={{ width: "100%", padding: "10px 14px", border: "none", borderRadius: 10, background: (!soDraft.date || !soDraft.amount) ? "#D4C4A8" : "linear-gradient(135deg, #B8860B, #D4A017)", color: "#FFF", fontWeight: 700, fontSize: 13, cursor: (!soDraft.date || !soDraft.amount) ? "not-allowed" : "pointer", boxShadow: (!soDraft.date || !soDraft.amount) ? "none" : "0 4px 12px rgba(184,134,11,0.25)" }}
                >
                  + Thêm vào bảng
                </button>
              </div>

              {soEntries.length === 0 && (
                <div style={{ textAlign: "center", padding: "20px 0", color: "#A0896C", fontSize: 12 }}>Chưa có dữ liệu. Nhập thông tin phía trên rồi bấm "Thêm vào bảng" để bắt đầu.</div>
              )}

              {soEntriesByMonth.map((group) => (
                <div key={group.key} style={{ background: "#FFFDF7", borderRadius: 12, border: "1px solid #F0E8D8", marginBottom: 10, overflow: "hidden" }}>
                  <div style={{ padding: "10px 12px", background: "linear-gradient(135deg, #FFF8E7, #FFFDF7)", borderBottom: "1px solid #F0E8D8", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: "#3D2E1F" }}>{group.label}</div>
                      <div style={{ fontSize: 10, color: "#A0896C" }}>{group.rows.length} ngày{group.rows.length !== group.entries.length ? ` (${group.entries.length} dòng nghiệp vụ)` : ""}</div>
                    </div>
                    <div style={{ display: "flex", gap: 10, fontSize: 10, color: "#8B7355", flexWrap: "wrap", justifyContent: "flex-end" }}>
                      <span>DT: <b style={{ fontFamily: "'DM Mono', monospace", color: "#3D2E1F" }}>{fmtM(group.total)}</b></span>
                      {(soType === "s2a" || soType === "s2b") && <span>GTGT: <b style={{ fontFamily: "'DM Mono', monospace", color: "#1565C0" }}>{fmtM(group.vat)}</b></span>}
                      {soType === "s2a" && <span>TNCN: <b style={{ fontFamily: "'DM Mono', monospace", color: "#B8860B" }}>{fmtM(group.pit)}</b></span>}
                    </div>
                  </div>
                  <div style={{ overflowX: soType === "s1a" ? "hidden" : "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", minWidth: soType === "s1a" ? 0 : soType === "s2d" ? 820 : 760, tableLayout: soType === "s1a" ? "fixed" : "auto" }}>
                      <thead>
                        <tr style={{ background: "#FAF6EE" }}>
                          <th style={{ padding: "7px 8px", fontSize: 10, color: "#8B7355", textAlign: "left", fontWeight: 600, width: soType === "s1a" ? "28%" : undefined }}>Ngày</th>
                          {(soType === "s2a" || soType === "s2b") && <th style={{ padding: "7px 8px", fontSize: 10, color: "#8B7355", textAlign: "left", fontWeight: 600 }}>Ngành</th>}
                          {(soType === "s2a" || soType === "s2b" || soType === "s2d") && <th style={{ padding: "7px 8px", fontSize: 10, color: "#8B7355", textAlign: "left", fontWeight: 600 }}>Số hiệu</th>}
                          <th style={{ padding: "7px 8px", fontSize: 10, color: "#8B7355", textAlign: "left", fontWeight: 600, width: soType === "s1a" ? "37%" : undefined }}>{soType === "s2d" ? "Tên VT/HH" : "Diễn giải"}</th>
                          {soType === "s2d" && <th style={{ padding: "7px 8px", fontSize: 10, color: "#8B7355", textAlign: "left", fontWeight: 600 }}>ĐVT</th>}
                          <th style={{ padding: "7px 8px", fontSize: 10, color: "#8B7355", textAlign: "right", fontWeight: 600, width: soType === "s1a" ? "35%" : undefined }}>{soType === "s2d" ? "Đơn giá" : "Số tiền"}</th>
                          {soType === "s2d" && <th style={{ padding: "7px 8px", fontSize: 10, color: "#2E7D32", textAlign: "right", fontWeight: 600 }}>Nhập SL</th>}
                          {soType === "s2d" && <th style={{ padding: "7px 8px", fontSize: 10, color: "#C62828", textAlign: "right", fontWeight: 600 }}>Xuất SL</th>}
                          {(soType === "s2a" || soType === "s2b") && <th style={{ padding: "7px 8px", fontSize: 10, color: "#1565C0", textAlign: "right", fontWeight: 600 }}>GTGT</th>}
                          {soType === "s2a" && <th style={{ padding: "7px 8px", fontSize: 10, color: "#B8860B", textAlign: "right", fontWeight: 600 }}>TNCN</th>}
                          <th style={{ padding: "7px 8px", fontSize: 10, color: "#C62828", textAlign: "center", fontWeight: 600 }}>Xóa</th>
                        </tr>
                      </thead>
                      <tbody>
                        {group.rows.map((entry) => {
                          const rowBg = entry.isNew ? "#FFFDE7" : "#FFF";
                          const cellStyle = { padding: soType === "s1a" ? "8px 6px" : "8px 8px", borderTop: "1px solid #F0E8D8", verticalAlign: "middle", background: rowBg, fontSize: 12, color: "#3D2E1F" };
                          const rowAmount = toMoneyNumber(entry.amount);
                          const moneyTextStyle = { display: "inline-block", maxWidth: "100%", padding: "4px 6px", borderRadius: 7, background: "#FFF8E7", textAlign: "right", whiteSpace: "nowrap", fontFamily: "'DM Mono', monospace", fontWeight: 700, color: "#3D2E1F", boxSizing: "border-box", overflow: "hidden", textOverflow: "clip", fontSize: soType === "s1a" ? 11 : 12 };
                          const descTextStyle = { display: "block", maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" };
                          const entryIndustry = entry.mixedIndustry ? null : BIZ_INDUSTRIES.find(ind => ind.id === (entry.industry || bizIndustry));
                          return (
                            <tr key={entry.key}>
                              <td style={{ ...cellStyle, minWidth: soType === "s1a" ? 0 : 110, position: "relative", whiteSpace: "nowrap" }}>
                                {entry.isNew && <div style={{ position: "absolute", top: 1, left: 6, background: "#FFD54F", color: "#5D4037", fontSize: 8, fontWeight: 700, padding: "1px 5px", borderRadius: 4 }}>MỚI</div>}
                                <span style={{ display: "block", marginTop: entry.isNew ? 12 : 0, fontFamily: "'DM Mono', monospace", fontWeight: 600 }}>{formatDateVN(entry.date)}</span>
                              </td>
                              {(soType === "s2a" || soType === "s2b") && (
                                <td style={{ ...cellStyle, minWidth: 150 }}>
                                  <span style={descTextStyle} title={entry.mixedIndustry ? "Nhiều ngành trong cùng ngày" : entryIndustry?.label || ""}>{entry.mixedIndustry ? "Nhiều ngành" : `${entryIndustry?.icon || ""} ${entryIndustry?.label || ""}`}</span>
                                </td>
                              )}
                              {(soType === "s2a" || soType === "s2b" || soType === "s2d") && (
                                <td style={{ ...cellStyle, minWidth: 90, whiteSpace: "nowrap" }}>
                                  {entry.note || "—"}
                                </td>
                              )}
                              <td style={{ ...cellStyle, minWidth: soType === "s1a" ? 0 : 190, maxWidth: soType === "s1a" ? "100%" : 260 }}>
                                <span style={descTextStyle} title={entry.desc || ""}>{entry.desc || "—"}</span>
                              </td>
                              {soType === "s2d" && (
                                <td style={{ ...cellStyle, minWidth: 70, whiteSpace: "nowrap" }}>
                                  {entry.unit || "—"}
                                </td>
                              )}
                              <td style={{ ...cellStyle, minWidth: soType === "s1a" ? 0 : 140, textAlign: "right" }}>
                                <span style={moneyTextStyle}>{fmt(rowAmount)}</span>
                              </td>
                              {soType === "s2d" && (
                                <td style={{ ...cellStyle, minWidth: 80 }}>
                                  <span style={{ ...moneyTextStyle, color: "#2E7D32" }}>{fmt(entry.inQty || 0)}</span>
                                </td>
                              )}
                              {soType === "s2d" && (
                                <td style={{ ...cellStyle, minWidth: 80 }}>
                                  <span style={{ ...moneyTextStyle, color: "#C62828" }}>{fmt(entry.outQty || 0)}</span>
                                </td>
                              )}
                              {(soType === "s2a" || soType === "s2b") && (
                                <td style={{ ...cellStyle, minWidth: 95, textAlign: "right", fontSize: 11, fontFamily: "'DM Mono', monospace", color: "#1565C0" }}>{fmt(entry.vatAmount || 0)}</td>
                              )}
                              {soType === "s2a" && (
                                <td style={{ ...cellStyle, minWidth: 95, textAlign: "right", fontSize: 11, fontFamily: "'DM Mono', monospace", color: "#B8860B" }}>{fmt(entry.pitAmount || 0)}</td>
                              )}
                              <td style={{ ...cellStyle, minWidth: 48, textAlign: "center" }}>
                                <button onClick={() => removeSoEntries(entry.indexes)} title={entry.lineCount > 1 ? `Xóa ${entry.lineCount} dòng cùng ngày` : "Xóa dòng"} style={{ width: 26, height: 26, border: "none", borderRadius: 6, background: "#FFEBEE", color: "#C62828", fontSize: 14, cursor: "pointer" }}>×</button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}

              {false && soEntries.map((entry, idx) => (
                <div key={idx} style={{
                  background: entry.isNew ? "#FFFDE7" : (idx % 2 === 0 ? "#FFFDF7" : "#FFF8E7"),
                  borderRadius: 12, padding: 12, marginBottom: 8,
                  border: entry.isNew ? "2px solid #FFD54F" : "1px solid #F0E8D8",
                  position: "relative",
                  boxShadow: entry.isNew ? "0 0 12px rgba(255,213,79,0.4)" : "none",
                  transition: "all 0.5s ease",
                }}>
                  {entry.isNew && <div style={{ position: "absolute", top: -8, left: 12, background: "#FFD54F", color: "#5D4037", fontSize: 9, fontWeight: 700, padding: "2px 8px", borderRadius: 4, letterSpacing: 0.5 }}>MỚI</div>}
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                    <span style={{ fontSize: 12, fontWeight: 600, color: "#8B7355" }}>#{soEntries.length - idx}</span>
                    <button onClick={() => removeSoEntry(idx)} style={{ width: 24, height: 24, border: "none", borderRadius: 6, background: "#FFEBEE", color: "#C62828", fontSize: 14, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>×</button>
                  </div>

                  {/* === S1a-HKD: Ngày (A) | Diễn giải (B) | Số tiền (1) === */}
                  {soType === "s1a" && (<>
                    <div style={{ overflow: "hidden", marginBottom: 6 }}>
                      <label style={{ fontSize: 10, color: "#8B7355" }}>Ngày tháng <span style={{ color: "#C4B49A" }}>(A)</span></label>
                      <input type="date" value={entry.date} onChange={e => updateSoEntry(idx, "date", e.target.value)} style={{ display: "block", width: "100%", padding: "8px 10px", border: "1px solid #D4C4A8", borderRadius: 6, fontSize: 14, background: "#FFF", boxSizing: "border-box", color: "#3D2E1F", WebkitAppearance: "none", MozAppearance: "none", appearance: "none", minHeight: 40, maxWidth: "100%" }} />
                    </div>
                    <div style={{ marginBottom: 6 }}>
                      <label style={{ fontSize: 10, color: "#8B7355" }}>Diễn giải <span style={{ color: "#C4B49A" }}>(B)</span></label>
                      <input type="text" value={entry.desc} onChange={e => updateSoEntry(idx, "desc", e.target.value)} placeholder="VD: Bán hàng tạp hóa, sửa xe..." style={{ width: "100%", padding: "6px 8px", border: "1px solid #D4C4A8", borderRadius: 6, fontSize: 12, background: "#FFF", boxSizing: "border-box" }} />
                    </div>
                    <div>
                      <label style={{ fontSize: 10, color: "#8B7355" }}>Số tiền <span style={{ color: "#C4B49A" }}>(1)</span></label>
                      <input ref={idx === 0 ? newAmountRef : null} type="text" inputMode="numeric" className="so-amount" value={entry.amount ? fmt(entry.amount) : ""} onChange={e => { const v = parseInt(e.target.value.replace(/\D/g, "")) || 0; updateSoEntry(idx, "amount", v); }} placeholder="Nhập số tiền..." style={{ display: "block", width: "100%", padding: "8px 10px", border: entry.isNew ? "2px solid #B8860B" : "1px solid #D4C4A8", borderRadius: 6, fontSize: 16, fontFamily: entry.amount ? "'DM Mono', monospace" : "'Noto Sans', sans-serif", fontWeight: entry.amount ? 600 : 400, background: "#FFF", boxSizing: "border-box", color: "#3D2E1F", minHeight: 40, maxWidth: "100%" }} />
                    </div>
                  </>)}

                  {/* === S2a-HKD: Ngành nghề + Số hiệu CT (A) | Ngày tháng (B) | Diễn giải (C) | Số tiền (1) === */}
                  {soType === "s2a" && (<>
                    <div style={{ marginBottom: 6 }}>
                      <label style={{ fontSize: 10, color: "#8B7355" }}>Ngành nghề</label>
                      <select value={entry.industry || bizIndustry} onChange={e => updateSoEntry(idx, "industry", e.target.value)} style={{ width: "100%", padding: "7px 8px", border: "1px solid #D4C4A8", borderRadius: 6, fontSize: 12, background: "#FFF", color: "#3D2E1F", boxSizing: "border-box" }}>
                        {BIZ_INDUSTRIES.map(ind => <option key={ind.id} value={ind.id}>{ind.icon} {ind.label} (GTGT {ind.vatRate*100}% / TNCN {ind.pitRate*100}%)</option>)}
                      </select>
                    </div>
                    <div style={{ display: "flex", gap: 6, marginBottom: 6 }}>
                      <div style={{ flex: "0 0 90px", overflow: "hidden" }}>
                        <label style={{ fontSize: 10, color: "#8B7355" }}>Số hiệu CT <span style={{ color: "#C4B49A" }}>(A)</span></label>
                        <input type="text" value={entry.note} onChange={e => updateSoEntry(idx, "note", e.target.value)} placeholder="HĐ001" style={{ width: "100%", padding: "6px 8px", border: "1px solid #D4C4A8", borderRadius: 6, fontSize: 12, background: "#FFF", boxSizing: "border-box" }} />
                      </div>
                      <div style={{ flex: 1, overflow: "hidden" }}>
                        <label style={{ fontSize: 10, color: "#8B7355" }}>Ngày tháng <span style={{ color: "#C4B49A" }}>(B)</span></label>
                        <input type="date" value={entry.date} onChange={e => updateSoEntry(idx, "date", e.target.value)} style={{ display: "block", width: "100%", padding: "6px 8px", border: "1px solid #D4C4A8", borderRadius: 6, fontSize: 13, background: "#FFF", boxSizing: "border-box", color: "#3D2E1F", WebkitAppearance: "none", MozAppearance: "none", appearance: "none", maxWidth: "100%" }} />
                      </div>
                    </div>
                    <div style={{ marginBottom: 6 }}>
                      <label style={{ fontSize: 10, color: "#8B7355" }}>Diễn giải <span style={{ color: "#C4B49A" }}>(C)</span></label>
                      <input type="text" value={entry.desc} onChange={e => updateSoEntry(idx, "desc", e.target.value)} placeholder="VD: Bán hàng thực phẩm..." style={{ width: "100%", padding: "6px 8px", border: "1px solid #D4C4A8", borderRadius: 6, fontSize: 12, background: "#FFF", boxSizing: "border-box" }} />
                    </div>
                    <div style={{ marginBottom: 4 }}>
                      <label style={{ fontSize: 10, color: "#8B7355" }}>Số tiền <span style={{ color: "#C4B49A" }}>(1)</span></label>
                      <input ref={idx === 0 ? newAmountRef : null} type="text" inputMode="numeric" className="so-amount" value={entry.amount ? fmt(entry.amount) : ""} onChange={e => { const v = parseInt(e.target.value.replace(/\D/g, "")) || 0; updateSoEntry(idx, "amount", v); }} placeholder="Nhập số tiền..." style={{ display: "block", width: "100%", padding: "8px 10px", border: entry.isNew ? "2px solid #B8860B" : "1px solid #D4C4A8", borderRadius: 6, fontSize: 16, fontFamily: entry.amount ? "'DM Mono', monospace" : "'Noto Sans', sans-serif", fontWeight: entry.amount ? 600 : 400, background: "#FFF", boxSizing: "border-box", color: "#3D2E1F", minHeight: 40, maxWidth: "100%" }} />
                    </div>
                    {entry.amount > 0 && (() => {
                      const eInd = BIZ_INDUSTRIES.find(x => x.id === (entry.industry || bizIndustry));
                      return (
                        <div style={{ display: "flex", gap: 8, fontSize: 10, color: "#8B7355", marginTop: 4, background: "#FFFDF7", padding: "5px 8px", borderRadius: 6, flexWrap: "wrap" }}>
                          <span>{eInd?.icon} {eInd?.label?.slice(0,15)}</span>
                          <span>GTGT {eInd?.vatRate*100}%: <b style={{ color: "#1565C0" }}>{fmt(entry.vatAmount)}</b></span>
                          <span>TNCN {eInd?.pitRate*100}%: <b style={{ color: "#B8860B" }}>{fmt(entry.pitAmount)}</b></span>
                        </div>
                      );
                    })()}
                  </>)}

                  {/* === S2b-HKD: like S2a but only GTGT, grouped by ngành === */}
                  {soType === "s2b" && (<>
                    <div style={{ marginBottom: 6 }}>
                      <label style={{ fontSize: 10, color: "#8B7355" }}>Ngành nghề</label>
                      <select value={entry.industry || bizIndustry} onChange={e => updateSoEntry(idx, "industry", e.target.value)} style={{ width: "100%", padding: "7px 8px", border: "1px solid #D4C4A8", borderRadius: 6, fontSize: 12, background: "#FFF", color: "#3D2E1F", boxSizing: "border-box" }}>
                        {BIZ_INDUSTRIES.map(ind => <option key={ind.id} value={ind.id}>{ind.icon} {ind.label} (GTGT {ind.vatRate*100}%)</option>)}
                      </select>
                    </div>
                    <div style={{ display: "flex", gap: 6, marginBottom: 6 }}>
                      <div style={{ flex: "0 0 90px", overflow: "hidden" }}>
                        <label style={{ fontSize: 10, color: "#8B7355" }}>Số hiệu CT <span style={{ color: "#C4B49A" }}>(A)</span></label>
                        <input type="text" value={entry.note} onChange={e => updateSoEntry(idx, "note", e.target.value)} placeholder="HĐ001" style={{ width: "100%", padding: "6px 8px", border: "1px solid #D4C4A8", borderRadius: 6, fontSize: 12, background: "#FFF", boxSizing: "border-box" }} />
                      </div>
                      <div style={{ flex: 1, overflow: "hidden" }}>
                        <label style={{ fontSize: 10, color: "#8B7355" }}>Ngày tháng <span style={{ color: "#C4B49A" }}>(B)</span></label>
                        <input type="date" value={entry.date} onChange={e => updateSoEntry(idx, "date", e.target.value)} style={{ display: "block", width: "100%", padding: "6px 8px", border: "1px solid #D4C4A8", borderRadius: 6, fontSize: 13, background: "#FFF", boxSizing: "border-box", color: "#3D2E1F", WebkitAppearance: "none", MozAppearance: "none", appearance: "none", maxWidth: "100%" }} />
                      </div>
                    </div>
                    <div style={{ marginBottom: 6 }}>
                      <label style={{ fontSize: 10, color: "#8B7355" }}>Diễn giải <span style={{ color: "#C4B49A" }}>(C)</span></label>
                      <input type="text" value={entry.desc} onChange={e => updateSoEntry(idx, "desc", e.target.value)} placeholder="VD: Bán hàng..." style={{ width: "100%", padding: "6px 8px", border: "1px solid #D4C4A8", borderRadius: 6, fontSize: 12, background: "#FFF", boxSizing: "border-box" }} />
                    </div>
                    <div>
                      <label style={{ fontSize: 10, color: "#8B7355" }}>Số tiền <span style={{ color: "#C4B49A" }}>(1)</span></label>
                      <input ref={idx === 0 ? newAmountRef : null} type="text" inputMode="numeric" className="so-amount" value={entry.amount ? fmt(entry.amount) : ""} onChange={e => { const v = parseInt(e.target.value.replace(/\D/g, "")) || 0; updateSoEntry(idx, "amount", v); }} placeholder="Nhập số tiền..." style={{ display: "block", width: "100%", padding: "8px 10px", border: entry.isNew ? "2px solid #B8860B" : "1px solid #D4C4A8", borderRadius: 6, fontSize: 16, fontFamily: entry.amount ? "'DM Mono', monospace" : "'Noto Sans', sans-serif", fontWeight: entry.amount ? 600 : 400, background: "#FFF", boxSizing: "border-box", color: "#3D2E1F", minHeight: 40, maxWidth: "100%" }} />
                    </div>
                    {entry.amount > 0 && (() => { const eInd = BIZ_INDUSTRIES.find(x => x.id === (entry.industry || bizIndustry)); return (
                      <div style={{ fontSize: 10, color: "#8B7355", marginTop: 4, background: "#FFFDF7", padding: "4px 8px", borderRadius: 6 }}>
                        {eInd?.icon} GTGT {eInd?.vatRate*100}%: <b style={{ color: "#1565C0" }}>{fmt(entry.vatAmount)}</b>
                      </div>); })()}
                  </>)}

                  {/* === S2c-HKD: Số hiệu CT (A) | Ngày (B) | Diễn giải (C) | Số tiền (1) — DT hoặc CP === */}
                  {soType === "s2c" && (<>
                    <div style={{ display: "flex", gap: 6, marginBottom: 6 }}>
                      <div style={{ flex: "0 0 90px", overflow: "hidden" }}>
                        <label style={{ fontSize: 10, color: "#8B7355" }}>Số hiệu CT <span style={{ color: "#C4B49A" }}>(A)</span></label>
                        <input type="text" value={entry.note} onChange={e => updateSoEntry(idx, "note", e.target.value)} placeholder="CT001" style={{ width: "100%", padding: "6px 8px", border: "1px solid #D4C4A8", borderRadius: 6, fontSize: 12, background: "#FFF", boxSizing: "border-box" }} />
                      </div>
                      <div style={{ flex: 1, overflow: "hidden" }}>
                        <label style={{ fontSize: 10, color: "#8B7355" }}>Ngày tháng <span style={{ color: "#C4B49A" }}>(B)</span></label>
                        <input type="date" value={entry.date} onChange={e => updateSoEntry(idx, "date", e.target.value)} style={{ display: "block", width: "100%", padding: "6px 8px", border: "1px solid #D4C4A8", borderRadius: 6, fontSize: 13, background: "#FFF", boxSizing: "border-box", color: "#3D2E1F", WebkitAppearance: "none", MozAppearance: "none", appearance: "none", maxWidth: "100%" }} />
                      </div>
                    </div>
                    <div style={{ marginBottom: 6 }}>
                      <label style={{ fontSize: 10, color: "#8B7355" }}>Loại</label>
                      <div style={{ display: "flex", gap: 6 }}>
                        {[{id:"dt",label:"Doanh thu"},{id:"cp",label:"Chi phí"}].map(t => (
                          <button key={t.id} onClick={() => updateSoEntry(idx, "entryType", t.id)} style={{ flex: 1, padding: "6px", border: (entry.entryType || "dt") === t.id ? "1.5px solid #B8860B" : "1px solid #D4C4A8", borderRadius: 6, background: (entry.entryType || "dt") === t.id ? "#FFF8E7" : "#FFF", fontSize: 11, fontWeight: (entry.entryType || "dt") === t.id ? 600 : 400, color: (entry.entryType || "dt") === t.id ? "#B8860B" : "#8B7355", cursor: "pointer" }}>{t.label}</button>
                        ))}
                      </div>
                    </div>
                    <div style={{ marginBottom: 6 }}>
                      <label style={{ fontSize: 10, color: "#8B7355" }}>Diễn giải <span style={{ color: "#C4B49A" }}>(C)</span></label>
                      <input type="text" value={entry.desc} onChange={e => updateSoEntry(idx, "desc", e.target.value)} placeholder={(entry.entryType || "dt") === "dt" ? "VD: Doanh thu bán hàng..." : "VD: Chi phí nguyên liệu, tiền lương..."} style={{ width: "100%", padding: "6px 8px", border: "1px solid #D4C4A8", borderRadius: 6, fontSize: 12, background: "#FFF", boxSizing: "border-box" }} />
                    </div>
                    <div>
                      <label style={{ fontSize: 10, color: "#8B7355" }}>Số tiền <span style={{ color: "#C4B49A" }}>(1)</span></label>
                      <input ref={idx === 0 ? newAmountRef : null} type="text" inputMode="numeric" className="so-amount" value={entry.amount ? fmt(entry.amount) : ""} onChange={e => { const v = parseInt(e.target.value.replace(/\D/g, "")) || 0; updateSoEntry(idx, "amount", v); }} placeholder="Nhập số tiền..." style={{ display: "block", width: "100%", padding: "8px 10px", border: entry.isNew ? "2px solid #B8860B" : "1px solid #D4C4A8", borderRadius: 6, fontSize: 16, fontFamily: entry.amount ? "'DM Mono', monospace" : "'Noto Sans', sans-serif", fontWeight: entry.amount ? 600 : 400, background: "#FFF", boxSizing: "border-box", color: (entry.entryType || "dt") === "cp" ? "#C62828" : "#3D2E1F", minHeight: 40, maxWidth: "100%" }} />
                    </div>
                  </>)}

                  {/* === S2d-HKD: Số hiệu (A) | Ngày (B) | Diễn giải (C) | ĐVT (D) | Đơn giá (1) | Nhập SL+Tiền | Xuất SL+Tiền === */}
                  {soType === "s2d" && (<>
                    <div style={{ display: "flex", gap: 6, marginBottom: 6 }}>
                      <div style={{ flex: "0 0 80px", overflow: "hidden" }}>
                        <label style={{ fontSize: 10, color: "#8B7355" }}>Số hiệu <span style={{ color: "#C4B49A" }}>(A)</span></label>
                        <input type="text" value={entry.note} onChange={e => updateSoEntry(idx, "note", e.target.value)} placeholder="PX01" style={{ width: "100%", padding: "6px 8px", border: "1px solid #D4C4A8", borderRadius: 6, fontSize: 12, background: "#FFF", boxSizing: "border-box" }} />
                      </div>
                      <div style={{ flex: 1, overflow: "hidden" }}>
                        <label style={{ fontSize: 10, color: "#8B7355" }}>Ngày tháng <span style={{ color: "#C4B49A" }}>(B)</span></label>
                        <input type="date" value={entry.date} onChange={e => updateSoEntry(idx, "date", e.target.value)} style={{ display: "block", width: "100%", padding: "6px 8px", border: "1px solid #D4C4A8", borderRadius: 6, fontSize: 13, background: "#FFF", boxSizing: "border-box", color: "#3D2E1F", WebkitAppearance: "none", MozAppearance: "none", appearance: "none", maxWidth: "100%" }} />
                      </div>
                    </div>
                    <div style={{ marginBottom: 6 }}>
                      <label style={{ fontSize: 10, color: "#8B7355" }}>Tên VT/HH <span style={{ color: "#C4B49A" }}>(C)</span></label>
                      <input type="text" value={entry.desc} onChange={e => updateSoEntry(idx, "desc", e.target.value)} placeholder="VD: Gạo tẻ, Xi măng, Phụ tùng..." style={{ width: "100%", padding: "6px 8px", border: "1px solid #D4C4A8", borderRadius: 6, fontSize: 12, background: "#FFF", boxSizing: "border-box" }} />
                    </div>
                    <div style={{ display: "flex", gap: 6, marginBottom: 6 }}>
                      <div style={{ flex: 1 }}>
                        <label style={{ fontSize: 10, color: "#8B7355" }}>ĐVT <span style={{ color: "#C4B49A" }}>(D)</span></label>
                        <input type="text" value={entry.unit || ""} onChange={e => updateSoEntry(idx, "unit", e.target.value)} placeholder="kg, cái..." style={{ width: "100%", padding: "6px 8px", border: "1px solid #D4C4A8", borderRadius: 6, fontSize: 12, background: "#FFF", boxSizing: "border-box" }} />
                      </div>
                      <div style={{ flex: 1 }}>
                        <label style={{ fontSize: 10, color: "#8B7355" }}>Đơn giá <span style={{ color: "#C4B49A" }}>(1)</span></label>
                        <input ref={idx === 0 ? newAmountRef : null} type="text" inputMode="numeric" value={entry.amount ? fmt(entry.amount) : ""} onChange={e => { const v = parseInt(e.target.value.replace(/\D/g, "")) || 0; updateSoEntry(idx, "amount", v); }} placeholder="0" style={{ width: "100%", padding: "6px 8px", border: "1px solid #D4C4A8", borderRadius: 6, fontSize: 12, fontFamily: "'DM Mono', monospace", background: "#FFF", boxSizing: "border-box" }} />
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 6, marginBottom: 6 }}>
                      <div style={{ flex: 1, background: "#E8F5E9", borderRadius: 6, padding: 6 }}>
                        <label style={{ fontSize: 10, color: "#2E7D32", fontWeight: 500 }}>Nhập SL <span style={{ color: "#A5D6A7" }}>(2)</span></label>
                        <input type="text" inputMode="numeric" value={entry.inQty || ""} onChange={e => updateSoEntry(idx, "inQty", parseInt(e.target.value)||0)} placeholder="0" style={{ width: "100%", padding: "4px 6px", border: "1px solid #A5D6A7", borderRadius: 4, fontSize: 12, fontFamily: "'DM Mono', monospace", background: "#FFF", boxSizing: "border-box" }} />
                      </div>
                      <div style={{ flex: 1, background: "#FFEBEE", borderRadius: 6, padding: 6 }}>
                        <label style={{ fontSize: 10, color: "#C62828", fontWeight: 500 }}>Xuất SL <span style={{ color: "#EF9A9A" }}>(4)</span></label>
                        <input type="text" inputMode="numeric" value={entry.outQty || ""} onChange={e => updateSoEntry(idx, "outQty", parseInt(e.target.value)||0)} placeholder="0" style={{ width: "100%", padding: "4px 6px", border: "1px solid #EF9A9A", borderRadius: 4, fontSize: 12, fontFamily: "'DM Mono', monospace", background: "#FFF", boxSizing: "border-box" }} />
                      </div>
                    </div>
                    {(entry.amount > 0) && (
                      <div style={{ display: "flex", gap: 8, fontSize: 10, color: "#8B7355", background: "#FFFDF7", padding: "4px 8px", borderRadius: 6 }}>
                        {(entry.inQty > 0) && <span>Nhập: <b style={{ color: "#2E7D32" }}>{fmt(entry.amount * entry.inQty)}</b></span>}
                        {(entry.outQty > 0) && <span>Xuất: <b style={{ color: "#C62828" }}>{fmt(entry.amount * entry.outQty)}</b></span>}
                      </div>
                    )}
                  </>)}
                </div>
              ))}
              </>)}

              {/* === S2c-HKD: Fixed form — Doanh thu + Chi phí hợp lý === */}
              {soType === "s2c" && (
                <div style={{ paddingTop: 8, borderTop: "1px solid #F0E8D8" }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "#3D2E1F", marginBottom: 10 }}>Nhập liệu S2c-HKD</div>

                  {/* Chứng từ + Kỳ kê khai */}
                  <div style={{ display: "flex", gap: 6, marginBottom: 10 }}>
                    <div style={{ flex: "0 0 90px", overflow: "hidden" }}>
                      <label style={{ fontSize: 10, color: "#8B7355" }}>Số hiệu CT <span style={{ color: "#C4B49A" }}>(A)</span></label>
                      <input type="text" value={s2cData.ctNo || ""} onChange={e => setS2cData({...s2cData, ctNo: e.target.value})} placeholder="CT001" style={{ width: "100%", padding: "6px 8px", border: "1px solid #D4C4A8", borderRadius: 6, fontSize: 12, background: "#FFF", boxSizing: "border-box" }} />
                    </div>
                    <div style={{ flex: 1, overflow: "hidden" }}>
                      <label style={{ fontSize: 10, color: "#8B7355" }}>Ngày tháng <span style={{ color: "#C4B49A" }}>(B)</span></label>
                      <input type="date" value={s2cData.date || new Date().toISOString().split("T")[0]} onChange={e => setS2cData({...s2cData, date: e.target.value})} style={{ display: "block", width: "100%", padding: "6px 8px", border: "1px solid #D4C4A8", borderRadius: 6, fontSize: 13, background: "#FFF", boxSizing: "border-box", color: "#3D2E1F", WebkitAppearance: "none", MozAppearance: "none", appearance: "none", maxWidth: "100%" }} />
                    </div>
                  </div>

                  <div style={{ background: "#E8F5E9", borderRadius: 10, padding: 12, marginBottom: 10 }}>
                    <label style={{ fontSize: 11, color: "#2E7D32", fontWeight: 600 }}>1. Doanh thu bán hàng hóa, dịch vụ <span style={{ color: "#A5D6A7" }}>(1)</span></label>
                    <input type="text" inputMode="numeric" className="so-amount" value={s2cData.revenue ? fmt(s2cData.revenue) : ""} onChange={e => setS2cData({...s2cData, revenue: parseInt(e.target.value.replace(/\D/g,""))||0})} placeholder="Nhập tổng doanh thu..." style={{ display: "block", width: "100%", padding: "8px 10px", border: "1px solid #A5D6A7", borderRadius: 6, fontSize: 15, fontFamily: s2cData.revenue ? "'DM Mono', monospace" : "'Noto Sans', sans-serif", fontWeight: s2cData.revenue ? 600 : 400, background: "#FFF", boxSizing: "border-box", color: "#2E7D32", marginTop: 4 }} />
                  </div>

                  <div style={{ background: "#FFEBEE", borderRadius: 10, padding: 12, marginBottom: 10 }}>
                    <label style={{ fontSize: 11, color: "#C62828", fontWeight: 600, marginBottom: 6, display: "block" }}>2. Chi phí hợp lý</label>
                    {[
                      { key: "cpA", label: "a) NVL, nhiên liệu, hàng hóa", ph: "Nguyên vật liệu, năng lượng..." },
                      { key: "cpB", label: "b) Tiền lương, phụ cấp, BHXH", ph: "Lương, công, bảo hiểm..." },
                      { key: "cpC", label: "c) Khấu hao TSCĐ", ph: "Khấu hao tài sản cố định..." },
                      { key: "cpD", label: "d) Dịch vụ mua ngoài", ph: "Điện, nước, thuê, sửa chữa..." },
                      { key: "cpE", label: "e) Chi phí khác", ph: "Lãi vay, chi phí trực tiếp khác..." },
                    ].map(cp => (
                      <div key={cp.key} style={{ marginBottom: 6 }}>
                        <label style={{ fontSize: 10, color: "#C62828" }}>{cp.label}</label>
                        <input type="text" inputMode="numeric" value={s2cData[cp.key] ? fmt(s2cData[cp.key]) : ""} onChange={e => setS2cData({...s2cData, [cp.key]: parseInt(e.target.value.replace(/\D/g,""))||0})} placeholder={cp.ph} style={{ width: "100%", padding: "6px 8px", border: "1px solid #EF9A9A", borderRadius: 6, fontSize: 12, fontFamily: "'DM Mono', monospace", background: "#FFF", boxSizing: "border-box", color: "#C62828" }} />
                      </div>
                    ))}
                    <div style={{ display: "flex", justifyContent: "space-between", paddingTop: 6, borderTop: "1px solid #EF9A9A" }}>
                      <span style={{ fontSize: 11, fontWeight: 600, color: "#C62828" }}>Tổng chi phí hợp lý (2)</span>
                      <span style={{ fontSize: 13, fontWeight: 700, fontFamily: "'DM Mono', monospace", color: "#C62828" }}>{fmt(s2cTotalCost)}</span>
                    </div>
                  </div>

                  <div style={{ background: "linear-gradient(135deg, #3D2E1F, #5D4230)", borderRadius: 10, padding: 12, color: "#FFF", marginBottom: 12 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                      <span style={{ fontSize: 11, color: "rgba(255,255,255,0.7)" }}>3. Chênh lệch (1) − (2)</span>
                      <span style={{ fontSize: 14, fontWeight: 700, fontFamily: "'DM Mono', monospace", color: "#4CAF50" }}>{fmt(s2cProfit)}</span>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                      <span style={{ fontSize: 11, color: "rgba(255,255,255,0.7)" }}>4. Thuế TNCN phải nộp</span>
                      <span style={{ fontSize: 14, fontWeight: 700, fontFamily: "'DM Mono', monospace", color: "#DAA520" }}>{fmt(Math.round(s2cProfit * (BIZ_INDUSTRIES.find(x=>x.id===bizIndustry)?.pitRate||0.015)))}</span>
                    </div>
                    <div style={{ fontSize: 9, color: "rgba(255,255,255,0.4)", marginTop: 4 }}>(3) × thuế suất TNCN {(BIZ_INDUSTRIES.find(x=>x.id===bizIndustry)?.pitRate||0)*100}%</div>
                  </div>

                  {/* Download buttons S2c */}
                  {s2cData.revenue > 0 && (
                    <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                      <button onClick={downloadSoXlsx} style={{ flex: 1, padding: "12px", borderRadius: 12, border: "none", background: "linear-gradient(135deg, #1565C0, #42A5F5)", color: "#FFF", fontSize: 13, fontWeight: 700, textAlign: "center", cursor: "pointer" }}>📥 Excel (.xlsx)</button>
                      <button onClick={printSoPdf} style={{ flex: 1, padding: "12px", borderRadius: 12, border: "none", background: "linear-gradient(135deg, #C62828, #EF5350)", color: "#FFF", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>📄 In / PDF</button>
                    </div>
                  )}
                </div>
              )}

              {/* === S2e-HKD: Fixed form — Tiền mặt + Tiền gửi NH === */}
              {soType === "s2e" && (
                <div style={{ paddingTop: 8, borderTop: "1px solid #F0E8D8" }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "#3D2E1F", marginBottom: 10 }}>Nhập liệu S2e-HKD</div>

                  {/* Chứng từ + Kỳ kê khai */}
                  <div style={{ display: "flex", gap: 6, marginBottom: 10 }}>
                    <div style={{ flex: "0 0 90px", overflow: "hidden" }}>
                      <label style={{ fontSize: 10, color: "#8B7355" }}>Số hiệu CT <span style={{ color: "#C4B49A" }}>(A)</span></label>
                      <input type="text" value={s2eData.ctNo || ""} onChange={e => setS2eData({...s2eData, ctNo: e.target.value})} placeholder="PT01" style={{ width: "100%", padding: "6px 8px", border: "1px solid #D4C4A8", borderRadius: 6, fontSize: 12, background: "#FFF", boxSizing: "border-box" }} />
                    </div>
                    <div style={{ flex: 1, overflow: "hidden" }}>
                      <label style={{ fontSize: 10, color: "#8B7355" }}>Ngày tháng <span style={{ color: "#C4B49A" }}>(B)</span></label>
                      <input type="date" value={s2eData.date || new Date().toISOString().split("T")[0]} onChange={e => setS2eData({...s2eData, date: e.target.value})} style={{ display: "block", width: "100%", padding: "6px 8px", border: "1px solid #D4C4A8", borderRadius: 6, fontSize: 13, background: "#FFF", boxSizing: "border-box", color: "#3D2E1F", WebkitAppearance: "none", MozAppearance: "none", appearance: "none", maxWidth: "100%" }} />
                    </div>
                  </div>

                  {/* Tiền mặt */}
                  <div style={{ background: "#FFF8E7", borderRadius: 10, padding: 12, marginBottom: 10, border: "1px solid #E8DCC8" }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: "#B8860B", marginBottom: 8 }}>💵 Tiền mặt</div>
                    {[
                      { key: "cashOpen", label: "Tiền mặt đầu kỳ", color: "#5D4E37" },
                      { key: "cashIn", label: "Tổng tiền thu vào trong kỳ (1)", color: "#2E7D32" },
                      { key: "cashOut", label: "Tổng tiền chi ra trong kỳ (2)", color: "#C62828" },
                    ].map(f => (
                      <div key={f.key} style={{ marginBottom: 6 }}>
                        <label style={{ fontSize: 10, color: f.color, fontWeight: 500 }}>{f.label}</label>
                        <input type="text" inputMode="numeric" value={s2eData[f.key] ? fmt(s2eData[f.key]) : ""} onChange={e => setS2eData({...s2eData, [f.key]: parseInt(e.target.value.replace(/\D/g,""))||0})} placeholder="0" style={{ width: "100%", padding: "6px 8px", border: `1px solid ${f.color === "#2E7D32" ? "#A5D6A7" : f.color === "#C62828" ? "#EF9A9A" : "#D4C4A8"}`, borderRadius: 6, fontSize: 13, fontFamily: "'DM Mono', monospace", fontWeight: 600, background: "#FFF", boxSizing: "border-box", color: f.color }} />
                      </div>
                    ))}
                    <div style={{ display: "flex", justifyContent: "space-between", paddingTop: 6, borderTop: "1px dashed #D4C4A8" }}>
                      <span style={{ fontSize: 11, fontWeight: 600, color: "#B8860B" }}>Tiền mặt tồn cuối kỳ</span>
                      <span style={{ fontSize: 14, fontWeight: 700, fontFamily: "'DM Mono', monospace", color: s2eCashClose >= 0 ? "#2E7D32" : "#C62828" }}>{fmt(s2eCashClose)}</span>
                    </div>
                  </div>

                  {/* Tiền gửi NH — nhiều ngân hàng */}
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: "#1565C0" }}>🏦 Tiền gửi không kỳ hạn ({s2eData.banks.length} NH)</div>
                    <button onClick={addS2eBank} style={{ padding: "4px 10px", border: "1.5px solid #1565C0", borderRadius: 6, background: "#E3F2FD", color: "#1565C0", fontWeight: 600, fontSize: 11, cursor: "pointer" }}>+ Thêm NH</button>
                  </div>
                  {s2eData.banks.map((bank, bi) => {
                    const bClose = bank.open + bank.in - bank.out;
                    return (
                    <div key={bi} style={{ background: "#E3F2FD", borderRadius: 10, padding: 12, border: "1px solid #90CAF9", marginBottom: 10, position: "relative" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                        <label style={{ fontSize: 10, color: "#1565C0", fontWeight: 600 }}>Ngân hàng {bi + 1}</label>
                        {s2eData.banks.length > 1 && <button onClick={() => removeS2eBank(bi)} style={{ width: 22, height: 22, border: "none", borderRadius: 4, background: "#FFCDD2", color: "#C62828", fontSize: 13, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>×</button>}
                      </div>
                      <input type="text" value={bank.name} onChange={e => updateS2eBank(bi, "name", e.target.value)} placeholder="VD: Vietcombank CN HCM" style={{ width: "100%", padding: "6px 8px", border: "1px solid #90CAF9", borderRadius: 6, fontSize: 12, background: "#FFF", boxSizing: "border-box", marginBottom: 6 }} />
                      {[
                        { key: "open", label: "Tiền gửi đầu kỳ", color: "#1565C0" },
                        { key: "in", label: "Tổng gửi vào trong kỳ (1)", color: "#2E7D32" },
                        { key: "out", label: "Tổng rút ra trong kỳ (2)", color: "#C62828" },
                      ].map(f => (
                        <div key={f.key} style={{ marginBottom: 6 }}>
                          <label style={{ fontSize: 10, color: f.color, fontWeight: 500 }}>{f.label}</label>
                          <input type="text" inputMode="numeric" value={bank[f.key] ? fmt(bank[f.key]) : ""} onChange={e => updateS2eBank(bi, f.key, parseInt(e.target.value.replace(/\D/g,""))||0)} placeholder="0" style={{ width: "100%", padding: "6px 8px", border: `1px solid ${f.color === "#2E7D32" ? "#A5D6A7" : f.color === "#C62828" ? "#EF9A9A" : "#90CAF9"}`, borderRadius: 6, fontSize: 13, fontFamily: "'DM Mono', monospace", fontWeight: 600, background: "#FFF", boxSizing: "border-box", color: f.color }} />
                        </div>
                      ))}
                      <div style={{ display: "flex", justifyContent: "space-between", paddingTop: 6, borderTop: "1px dashed #90CAF9" }}>
                        <span style={{ fontSize: 11, fontWeight: 600, color: "#1565C0" }}>Tiền gửi cuối kỳ</span>
                        <span style={{ fontSize: 14, fontWeight: 700, fontFamily: "'DM Mono', monospace", color: bClose >= 0 ? "#2E7D32" : "#C62828" }}>{fmt(bClose)}</span>
                      </div>
                    </div>);
                  })}

                  {/* Tổng hợp tất cả NH */}
                  {s2eData.banks.length > 1 && (
                    <div style={{ background: "linear-gradient(135deg, #1A237E, #283593)", borderRadius: 10, padding: 12, color: "#FFF", marginBottom: 12 }}>
                      <div style={{ fontSize: 11, color: "#90CAF9", fontWeight: 600, marginBottom: 6 }}>Tổng hợp tiền gửi ({s2eData.banks.length} NH)</div>
                      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11 }}>
                        <span style={{ color: "rgba(255,255,255,0.6)" }}>Tổng gửi vào</span>
                        <span style={{ fontFamily: "'DM Mono', monospace", fontWeight: 600, color: "#4CAF50" }}>{fmt(s2eData.banks.reduce((s,b) => s+b.in, 0))}</span>
                      </div>
                      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11 }}>
                        <span style={{ color: "rgba(255,255,255,0.6)" }}>Tổng rút ra</span>
                        <span style={{ fontFamily: "'DM Mono', monospace", fontWeight: 600, color: "#EF5350" }}>{fmt(s2eData.banks.reduce((s,b) => s+b.out, 0))}</span>
                      </div>
                      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, paddingTop: 4, borderTop: "1px solid rgba(255,255,255,0.2)", marginTop: 4 }}>
                        <span style={{ color: "#90CAF9", fontWeight: 600 }}>Tổng tồn cuối kỳ</span>
                        <span style={{ fontFamily: "'DM Mono', monospace", fontWeight: 700, color: "#FFF" }}>{fmt(s2eData.banks.reduce((s,b) => s+b.open+b.in-b.out, 0))}</span>
                      </div>
                    </div>
                  )}

                  {/* Download buttons S2e */}
                  {(s2eData.cashIn > 0 || s2eData.cashOut > 0 || s2eData.banks.some(b => b.in > 0 || b.out > 0)) && (
                    <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                      <button onClick={downloadSoXlsx} style={{ flex: 1, padding: "12px", borderRadius: 12, border: "none", background: "linear-gradient(135deg, #1565C0, #42A5F5)", color: "#FFF", fontSize: 13, fontWeight: 700, textAlign: "center", cursor: "pointer" }}>📥 Excel (.xlsx)</button>
                      <button onClick={printSoPdf} style={{ flex: 1, padding: "12px", borderRadius: 12, border: "none", background: "linear-gradient(135deg, #C62828, #EF5350)", color: "#FFF", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>📄 In / PDF</button>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div style={{ background: "#E3F2FD", borderRadius: 14, padding: 14, border: "1px solid #90CAF9" }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: "#1565C0", marginBottom: 4 }}>💡 Hướng dẫn ghi sổ</div>
              <div style={{ fontSize: 11, color: "#1A237E", lineHeight: 1.6 }}>
                • Ghi theo từng nghiệp vụ phát sinh hoặc tổng hợp theo ngày
                <br/>• <b>S1a</b>: Chỉ cần ghi ngày + doanh thu, dùng cho HKD doanh thu không quá 1 tỷ
                <br/>• <b>S2a</b>: Tự động tính thuế GTGT & TNCN theo ngành đã chọn ở tab Tính thuế
                <br/>• <b>S2b</b>: Ghi cả doanh thu + chi phí để tính lợi nhuận
                <br/>• Sổ kế toán lưu trữ tối thiểu <b>5 năm</b>
                <br/>• Có thể lưu bản điện tử hoặc in giấy
              </div>
            </div>
          </div>)}

          {/* Sub-tab: Tờ khai & Sổ sách */}
          {hkdSubTab === "tokhai" && (<div>

          {/* Bảng phân loại biểu mẫu theo nhóm */}
          <div style={{ background: "linear-gradient(135deg, #3D2E1F, #5D4230)", borderRadius: 16, padding: 16, marginBottom: 16, color: "#FFF" }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: "#DAA520", marginBottom: 10 }}>📋 Biểu mẫu theo nhóm HKD (TT 50/2026 & TT 152/2025)</div>
            {[
              { nhom: "Nhóm 1: DT ≤ 1 tỷ", tokhai: "01/TKN-CNKD", so: "S1a-HKD", desc: "Thông báo doanh thu • Sổ doanh thu đơn giản", color: "#4CAF50" },
              { nhom: "Nhóm 2: Trên 1 tỷ → 3 tỷ", tokhai: "01/CNKD", so: "S2a-HKD", desc: "Tờ khai GTGT & TNCN • Sổ DT theo ngành nghề", color: "#FFC107" },
              { nhom: "Nhóm 3: 3 tỷ → 50 tỷ", tokhai: "01/CNKD + QTT", so: "S2b-HKD + S2c/S2d/S2e", desc: "Khai GTGT/TNCN + QT năm • Sổ DT, chi phí, tiền, vật tư", color: "#FF7043" },
              { nhom: "Nhóm 4: > 50 tỷ", tokhai: "01/CNKD + QTT", so: "Chế độ KT DN siêu nhỏ", desc: "Khai tháng/quý + QT năm • KT theo TT 132/2018", color: "#EF5350" },
            ].map((r, i) => (
              <div key={i} style={{ padding: "10px 0", borderBottom: i < 3 ? "1px solid rgba(255,255,255,0.1)" : "none" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: r.color }}>{r.nhom}</span>
                </div>
                <div style={{ display: "flex", gap: 8, marginBottom: 2 }}>
                  <span style={{ fontSize: 10, color: "rgba(255,255,255,0.5)" }}>Tờ khai:</span>
                  <span style={{ fontSize: 11, color: "#DAA520", fontWeight: 600, fontFamily: "'DM Mono', monospace" }}>{r.tokhai}</span>
                </div>
                <div style={{ display: "flex", gap: 8, marginBottom: 2 }}>
                  <span style={{ fontSize: 10, color: "rgba(255,255,255,0.5)" }}>Sổ KT:</span>
                  <span style={{ fontSize: 11, color: "#90CAF9", fontWeight: 600, fontFamily: "'DM Mono', monospace" }}>{r.so}</span>
                </div>
                <div style={{ fontSize: 10, color: "rgba(255,255,255,0.45)" }}>{r.desc}</div>
              </div>
            ))}
            <div style={{ fontSize: 10, color: "rgba(255,255,255,0.35)", marginTop: 8 }}>Kèm 01/BK-STK (thông báo tài khoản NH) cho tất cả các nhóm</div>
          </div>

          {/* Form tờ khai */}
          <div style={{ background: "#FFF", borderRadius: 18, padding: 20, boxShadow: "0 2px 12px rgba(0,0,0,0.04)", marginBottom: 16 }}>
            <div style={{ fontSize: 15, fontWeight: 600, color: "#3D2E1F", marginBottom: 4 }}>Lập Tờ Khai Thuế HKD</div>
            <div style={{ fontSize: 12, color: "#A0896C", marginBottom: 16 }}>Mẫu 01/CNKD theo Thông tư 50/2026 (Nhóm 2-4) hoặc 01/TKN-CNKD (Nhóm 1)</div>

            {/* Kỳ thuế */}
            <label style={{ display: "block", fontSize: 13, color: "#8B7355", marginBottom: 6, fontWeight: 500 }}>Kỳ tính thuế</label>
            <div style={{ display: "flex", gap: 6, marginBottom: 14 }}>
              {["q1", "q2", "q3", "q4"].map(q => (
                <button key={q} onClick={() => { setTkKyThue(q); setTkDoanhthu([0,0,0]); }} style={{ flex: 1, padding: "8px 4px", border: tkKyThue === q ? "1.5px solid #B8860B" : "1.5px solid #E8DCC8", borderRadius: 10, background: tkKyThue === q ? "#FFF8E7" : "#FFFDF7", fontSize: 12, fontWeight: tkKyThue === q ? 600 : 400, color: tkKyThue === q ? "#B8860B" : "#8B7355", cursor: "pointer" }}>
                  {tkQuarterLabels[q]}
                </button>
              ))}
            </div>

            {/* Thông tin NNT */}
            <div style={{ fontSize: 13, fontWeight: 600, color: "#3D2E1F", marginBottom: 10, paddingTop: 8, borderTop: "1px solid #F0E8D8" }}>I. Thông tin người nộp thuế</div>
            {[
              { label: "Mã số thuế", val: tkMST, set: setTkMST, ph: "VD: 0123456789" },
              { label: "Họ và tên", val: tkHoTen, set: setTkHoTen, ph: "Nguyễn Văn A" },
              { label: "Địa chỉ trụ sở KD", val: tkDiaChiKD, set: setTkDiaChiKD, ph: "Số 1, Đường ABC, Quận X" },
              { label: "Điện thoại", val: tkDienThoai, set: setTkDienThoai, ph: "0901234567" },
              { label: "Email", val: tkEmail, set: setTkEmail, ph: "email@example.com" },
            ].map((f, i) => (
              <div key={i} style={{ marginBottom: 10 }}>
                <label style={{ display: "block", fontSize: 12, color: "#8B7355", marginBottom: 3 }}>{f.label}</label>
                <input type="text" value={f.val} onChange={e => f.set(e.target.value)} placeholder={f.ph} style={{ width: "100%", padding: "8px 12px", border: "1.5px solid #D4C4A8", borderRadius: 8, fontSize: 14, background: "#FFFDF7", color: "#3D2E1F", outline: "none", boxSizing: "border-box" }} onFocus={e => e.target.style.borderColor = "#B8860B"} onBlur={e => e.target.style.borderColor = "#D4C4A8"} />
              </div>
            ))}

            {/* Ngành nghề */}
            <label style={{ display: "block", fontSize: 12, color: "#8B7355", marginBottom: 3 }}>Nhóm ngành nghề kinh doanh</label>
            <select value={tkNganhNghe} onChange={e => setTkNganhNghe(e.target.value)} style={{ width: "100%", padding: "8px 12px", border: "1.5px solid #D4C4A8", borderRadius: 8, fontSize: 13, background: "#FFFDF7", color: "#3D2E1F", marginBottom: 14, outline: "none", boxSizing: "border-box" }}>
              {BIZ_INDUSTRIES.map(ind => <option key={ind.id} value={ind.id}>{ind.icon} {ind.label} (GTGT {ind.vatRate*100}% / TNCN {ind.pitRate*100}%)</option>)}
            </select>

            {/* Doanh thu theo tháng */}
            <div style={{ fontSize: 13, fontWeight: 600, color: "#3D2E1F", marginBottom: 10, paddingTop: 8, borderTop: "1px solid #F0E8D8" }}>II. Doanh thu & Thuế</div>
            {tkMonths.map((m, i) => (
              <MoneyInput key={m} label={`Doanh thu tháng ${m}`} value={tkDoanhthu[i]} onChange={v => { const n = [...tkDoanhthu]; n[i] = v; setTkDoanhthu(n); }} placeholder="0" />
            ))}

            {/* Mức trừ miễn thuế TNCN cho quý này */}
            {tkTotalRevenue > 0 && (
              <div style={{ background: "#FFF8E7", borderRadius: 12, padding: 12, border: "1px solid #E8DCC8", marginBottom: 12 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                  <label style={{ fontSize: 12, color: "#8B7355", fontWeight: 500 }}>Mức trừ miễn thuế TNCN quý này</label>
                </div>
                <MoneyInput value={tkExemptAmount} onChange={v => setTkExemptAmount(Math.min(v, BIZ_TAX_EXEMPT))} placeholder="250.000.000" />
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  {[
                    { label: "Dàn đều (250tr)", val: BIZ_TAX_QUARTER_EXEMPT },
                    { label: "Trừ hết Q này (1 tỷ)", val: BIZ_TAX_EXEMPT },
                    { label: "Không trừ (0)", val: 0 },
                  ].map(opt => (
                    <button key={opt.val} onClick={() => setTkExemptAmount(opt.val)} style={{
                      padding: "5px 10px", border: tkExemptAmount === opt.val ? "1.5px solid #B8860B" : "1px solid #D4C4A8",
                      borderRadius: 6, fontSize: 11, cursor: "pointer", transition: "all 0.15s",
                      background: tkExemptAmount === opt.val ? "#B8860B" : "#FFFDF7",
                      color: tkExemptAmount === opt.val ? "#FFF" : "#8B7355",
                      fontWeight: tkExemptAmount === opt.val ? 600 : 400,
                    }}>{opt.label}</button>
                  ))}
                </div>
                <div style={{ fontSize: 10, color: "#A0896C", marginTop: 6, lineHeight: 1.5 }}>
                  Ngưỡng miễn thuế TNCN cả năm: 1 tỷ. Bạn được chọn phân bổ vào quý nào, tổng cả năm không quá 1 tỷ.
                </div>
              </div>
            )}

            {/* Nhóm indicator + mẫu áp dụng */}
            {tkTotalRevenue > 0 && (() => {
              const grp = tkAnnualEstimate <= BIZ_TAX_EXEMPT ? 1 : tkAnnualEstimate <= 3_000_000_000 ? 2 : tkAnnualEstimate <= 50_000_000_000 ? 3 : 4;
              const formInfo = grp === 1
                ? { form: "01/TKN-CNKD", so: "S1a-HKD", note: "Chỉ thông báo doanh thu, không phải nộp thuế GTGT & TNCN", color: "#4CAF50" }
                : { form: "01/CNKD", so: grp === 2 ? "S2a-HKD" : "S2b-HKD + S2c/S2d/S2e", note: "Kê khai thuế GTGT & tạm nộp thuế TNCN theo quý", color: "#B8860B" };
              return (<>
                <div style={{ background: grp === 1 ? "#E8F5E9" : "#FFF8E7", borderRadius: 12, padding: 12, border: `1px solid ${grp === 1 ? "#A5D6A7" : "#E8DCC8"}`, marginBottom: 8 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: formInfo.color, marginBottom: 4 }}>
                    {grp === 1 ? "✅" : "📊"} Nhóm {grp} (DT năm ước tính: {fmtM(tkAnnualEstimate)})
                  </div>
                  <div style={{ display: "flex", gap: 12 }}>
                    <div style={{ fontSize: 11, color: "#5D4E37" }}>Tờ khai: <b style={{ fontFamily: "'DM Mono', monospace", color: formInfo.color }}>{formInfo.form}</b></div>
                    <div style={{ fontSize: 11, color: "#5D4E37" }}>Sổ KT: <b style={{ fontFamily: "'DM Mono', monospace", color: "#1565C0" }}>{formInfo.so}</b></div>
                  </div>
                  <div style={{ fontSize: 10, color: "#8B7355", marginTop: 4 }}>{formInfo.note}</div>
                </div>

                {grp > 1 && (
                  <div style={{ background: "#FFF8E7", borderRadius: 12, padding: 12, border: "1px solid #E8DCC8", marginBottom: 14 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                      <span style={{ fontSize: 12, color: "#8B7355" }}>Tổng doanh thu quý</span>
                      <span style={{ fontSize: 13, fontWeight: 600, fontFamily: "'DM Mono', monospace", color: "#3D2E1F" }}>{fmt(tkTotalRevenue)}</span>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                      <span style={{ fontSize: 12, color: "#8B7355" }}>Thuế GTGT ({tkIndustry.vatRate*100}% × DT)</span>
                      <span style={{ fontSize: 13, fontWeight: 600, fontFamily: "'DM Mono', monospace", color: "#1565C0" }}>{fmt(tkVat)}</span>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4, paddingTop: 6, borderTop: "1px dashed #E8DCC8" }}>
                      <span style={{ fontSize: 11, color: "#A0896C" }}>Trừ miễn thuế TNCN quý này</span>
                      <span style={{ fontSize: 12, fontFamily: "'DM Mono', monospace", color: "#CD853F" }}>−{fmt(tkQuarterExempt)}</span>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                      <span style={{ fontSize: 11, color: "#A0896C" }}>DT tính thuế TNCN</span>
                      <span style={{ fontSize: 12, fontFamily: "'DM Mono', monospace", color: "#5D4E37" }}>{fmt(tkTaxableRevenue)}</span>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                      <span style={{ fontSize: 12, color: "#8B7355", fontWeight: 600 }}>Tạm nộp TNCN ({tkIndustry.pitRate*100}%)</span>
                      <span style={{ fontSize: 13, fontWeight: 600, fontFamily: "'DM Mono', monospace", color: "#B8860B" }}>{fmt(tkPit)}</span>
                    </div>
                  </div>
                )}
              </>);
            })()}

            {/* Tài khoản ngân hàng */}
            <div style={{ fontSize: 13, fontWeight: 600, color: "#3D2E1F", marginBottom: 10, paddingTop: 8, borderTop: "1px solid #F0E8D8" }}>Tài khoản NH (01/BK-STK)</div>
            {[
              { label: "Số tài khoản", val: tkSTK, set: setTkSTK, ph: "VD: 1234567890" },
              { label: "Tại ngân hàng", val: tkNganHang, set: setTkNganHang, ph: "VD: Vietcombank CN HCM" },
            ].map((f, i) => (
              <div key={i} style={{ marginBottom: 10 }}>
                <label style={{ display: "block", fontSize: 12, color: "#8B7355", marginBottom: 3 }}>{f.label}</label>
                <input type="text" value={f.val} onChange={e => f.set(e.target.value)} placeholder={f.ph} style={{ width: "100%", padding: "8px 12px", border: "1.5px solid #D4C4A8", borderRadius: 8, fontSize: 14, background: "#FFFDF7", color: "#3D2E1F", outline: "none", boxSizing: "border-box" }} onFocus={e => e.target.style.borderColor = "#B8860B"} onBlur={e => e.target.style.borderColor = "#D4C4A8"} />
              </div>
            ))}

            {/* Export - single tap download */}
            {tkCanDownload ? (
              <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                <button onClick={downloadTkXlsx} style={{
                  flex: 1, padding: "14px", borderRadius: 14, border: "none",
                  background: "linear-gradient(135deg, #B8860B, #DAA520)", color: "#FFF", fontSize: 14,
                  fontWeight: 700, textAlign: "center", cursor: "pointer",
                  boxShadow: "0 4px 12px rgba(184,134,11,0.3)",
                }}>
                  📥 Excel (.xlsx)
                </button>
                <button onClick={printTkPdf} style={{
                  flex: 1, padding: "14px", borderRadius: 14, border: "none",
                  background: "linear-gradient(135deg, #C62828, #EF5350)", color: "#FFF", fontSize: 14,
                  fontWeight: 700, textAlign: "center", cursor: "pointer",
                  boxShadow: "0 4px 12px rgba(198,40,40,0.3)",
                }}>
                  📄 In / Lưu PDF
                </button>
              </div>
            ) : (
              <div style={{
                width: "100%", padding: "14px", borderRadius: 14, marginTop: 8,
                background: "#D4C4A8", color: "#FFF", fontSize: 15, fontWeight: 700,
                textAlign: "center", boxSizing: "border-box", cursor: "not-allowed",
              }}>
                📥 {!tkMST || !tkHoTen
                  ? "Tải về (điền MST & Họ tên)"
                  : tkTotalRevenue <= 0
                    ? "Tải về (nhập doanh thu)"
                    : "Nhóm 1 sử dụng mẫu 01/TKN-CNKD"}
              </div>
            )}
          </div>

          {/* Info box */}
          <div style={{ background: "#FFF", borderRadius: 16, padding: 16, boxShadow: "0 2px 12px rgba(0,0,0,0.04)", marginBottom: 12 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: "#3D2E1F", marginBottom: 8 }}>📌 Hệ thống sổ kế toán HKD 2026</div>
            <div style={{ fontSize: 11, color: "#5D4E37", lineHeight: 1.7 }}>
              Theo <b>Thông tư 152/2025/TT-BTC</b>, hộ kinh doanh sử dụng 7 loại sổ:
            </div>
            {[
              { ma: "S1a-HKD", ten: "Sổ DT bán hàng (nhóm 1)", who: "HKD doanh thu không quá 1 tỷ — bắt buộc duy nhất" },
              { ma: "S2a-HKD", ten: "Sổ DT theo ngành nghề", who: "HKD nộp thuế % trên DT (nhóm 2)" },
              { ma: "S2b-HKD", ten: "Sổ DT (PP theo thu nhập)", who: "HKD tính thuế theo lợi nhuận (nhóm 2-3)" },
              { ma: "S2c-HKD", ten: "Sổ chi tiết doanh thu, chi phí", who: "HKD nhóm 3+ cần theo dõi chi phí" },
              { ma: "S2d-HKD", ten: "Sổ chi tiết vật liệu, hàng hóa", who: "HKD có hàng tồn kho" },
              { ma: "S2e-HKD", ten: "Sổ chi tiết tiền", who: "HKD theo dõi thu chi tiền mặt/NH" },
              { ma: "S3a-HKD", ten: "Sổ theo dõi thuế khác", who: "HKD có thuế TTĐB, tài nguyên..." },
            ].map((s, i) => (
              <div key={i} style={{ display: "flex", gap: 8, padding: "6px 0", borderBottom: i < 6 ? "1px solid #F0E8D8" : "none" }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: "#1565C0", fontFamily: "'DM Mono', monospace", minWidth: 72 }}>{s.ma}</span>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 500, color: "#3D2E1F" }}>{s.ten}</div>
                  <div style={{ fontSize: 10, color: "#A0896C" }}>{s.who}</div>
                </div>
              </div>
            ))}
          </div>

          <div style={{ background: "#E8F5E9", borderRadius: 14, padding: 14, border: "1px solid #A5D6A7" }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: "#2E7D32", marginBottom: 4 }}>💡 Lưu ý quan trọng</div>
            <div style={{ fontSize: 11, color: "#33691E", lineHeight: 1.6 }}>
              • HKD doanh thu không quá 1 tỷ vẫn <b>bắt buộc</b> ghi sổ S1a-HKD dù miễn thuế
              <br/>• Nộp tờ khai trên <b>eTax Mobile</b> hoặc Cổng thuế điện tử
              <br/>• Hạn nộp quý: ngày cuối tháng đầu quý sau (Q1/2026 gia hạn 30/04)
              <br/>• 01/BK-STK nộp kèm tờ khai đầu tiên, hạn 20/04/2026
              <br/>• Lưu trữ sổ sách tối thiểu 5 năm
            </div>
          </div>
        </div>)}

        </div>)}

        {/* ===== DOANH NGHIỆP TAB ===== */}
        {activeTab === "dn" && (<div>
          <div style={{ background: "#FFF", borderRadius: 18, padding: 20, boxShadow: "0 2px 12px rgba(0,0,0,0.04)", marginBottom: 16 }}>
            <div style={{ fontSize: 15, fontWeight: 600, color: "#3D2E1F", marginBottom: 4 }}>Thuế Thu Nhập Doanh Nghiệp (TNDN)</div>
            <div style={{ fontSize: 12, color: "#A0896C", marginBottom: 14 }}>Luật Thuế TNDN 2025 & Nghị định 320/2025/NĐ-CP</div>

            {/* Thuế suất reference */}
            <div style={{ background: "linear-gradient(135deg, #1A237E, #283593)", borderRadius: 14, padding: 14, marginBottom: 16, color: "#FFF" }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: "#90CAF9", marginBottom: 8 }}>📌 Thuế suất TNDN 2026</div>
              {[
                { rate: "15%", cond: "DT năm ≤ 3 tỷ đồng", color: "#4CAF50" },
                { rate: "17%", cond: "DT năm 3 tỷ → 50 tỷ đồng", color: "#FFC107" },
                { rate: "20%", cond: "DT năm > 50 tỷ (thuế suất chung)", color: "#FF7043" },
              ].map((r, i) => (
                <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "5px 0", borderBottom: i < 2 ? "1px solid rgba(255,255,255,0.1)" : "none" }}>
                  <span style={{ fontSize: 12, color: "rgba(255,255,255,0.75)" }}>{r.cond}</span>
                  <span style={{ fontSize: 14, fontWeight: 700, color: r.color, fontFamily: "'DM Mono', monospace" }}>{r.rate}</span>
                </div>
              ))}
            </div>

            {/* Inputs */}
            <MoneyInput label="Tổng doanh thu năm" value={dnRevenue} onChange={setDnRevenue} placeholder="VD: 5.000.000.000" />
            <MoneyInput label="Tổng chi phí được trừ" value={dnCost} onChange={setDnCost} placeholder="VD: 4.000.000.000" />
            <MoneyInput label="Thu nhập khác (lãi tiền gửi, thanh lý TS...)" value={dnOtherIncome} onChange={setDnOtherIncome} placeholder="0" />
            <MoneyInput label="Thu nhập miễn thuế" value={dnExemptIncome} onChange={setDnExemptIncome} placeholder="0" />
            <MoneyInput label="Lỗ kết chuyển từ năm trước" value={dnLossCarry} onChange={setDnLossCarry} placeholder="0" />
          </div>

          {/* TNDN Results */}
          {dnRevenue > 0 && (<div style={{ animation: "fadeIn 0.3s ease" }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 8 }}>
              <ResultCard label="Thuế TNDN phải nộp" value={fmt(dnTndn)} accent color="linear-gradient(135deg, #1A237E, #3949AB)" sub={`Thuế suất: ${dnTaxRate * 100}%`} />
              <ResultCard label="Lợi nhuận sau thuế" value={fmt(dnProfit)} sub={`Tỷ suất: ${dnRevenue > 0 ? (dnProfit / dnRevenue * 100).toFixed(1) : 0}%`} />
            </div>

            {/* Detail breakdown */}
            <div style={{ background: "#FFF", borderRadius: 16, padding: 16, boxShadow: "0 2px 12px rgba(0,0,0,0.04)", marginBottom: 12 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: "#3D2E1F", marginBottom: 10 }}>Chi tiết tính thuế TNDN</div>
              {[
                { label: "Doanh thu", val: dnRevenue, bold: true },
                { label: "Chi phí được trừ", val: dnCost, sub: true },
                ...(dnOtherIncome > 0 ? [{ label: "Thu nhập khác", val: dnOtherIncome }] : []),
                ...(dnExemptIncome > 0 ? [{ label: "Thu nhập miễn thuế", val: dnExemptIncome, sub: true }] : []),
                ...(dnLossCarry > 0 ? [{ label: "Lỗ kết chuyển", val: dnLossCarry, sub: true }] : []),
                { label: "Thu nhập tính thuế", val: dnTaxableIncome, bold: true, color: "#1A237E" },
                { label: `Thuế TNDN (${dnTaxRate * 100}%)`, val: dnTndn, bold: true, color: "#C62828" },
                { label: "Lợi nhuận sau thuế", val: dnProfit, bold: true, color: "#2E7D32" },
              ].map((row, i) => (
                <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: "1px solid #F5F0E5" }}>
                  <span style={{ fontSize: 12, color: row.bold ? "#3D2E1F" : "#8B7355", fontWeight: row.bold ? 600 : 400 }}>{row.label}</span>
                  <span style={{ fontSize: 13, fontFamily: "'DM Mono', monospace", color: row.color || (row.sub ? "#CD853F" : "#3D2E1F"), fontWeight: row.bold ? 600 : 400 }}>{row.sub ? "−" : ""}{fmt(row.val)}</span>
                </div>
              ))}
            </div>

            {/* Thuế GTGT */}
            <div style={{ background: "#FFF", borderRadius: 16, padding: 16, boxShadow: "0 2px 12px rgba(0,0,0,0.04)", marginBottom: 12 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: "#3D2E1F", marginBottom: 10 }}>Thuế GTGT doanh nghiệp</div>
              <div style={{ display: "flex", gap: 6, marginBottom: 12 }}>
                {[{ id: "deduct", label: "Phương pháp khấu trừ", desc: "ĐR − ĐV" }, { id: "direct", label: "Phương pháp trực tiếp", desc: "% × DT" }].map(m => (
                  <button key={m.id} onClick={() => setDnVatMethod(m.id)} style={{ flex: 1, padding: "8px", border: dnVatMethod === m.id ? "1.5px solid #1565C0" : "1.5px solid #E8DCC8", borderRadius: 10, background: dnVatMethod === m.id ? "#E3F2FD" : "#FFFDF7", cursor: "pointer", textAlign: "center" }}>
                    <div style={{ fontSize: 11, fontWeight: dnVatMethod === m.id ? 600 : 400, color: dnVatMethod === m.id ? "#1565C0" : "#5D4E37" }}>{m.label}</div>
                    <div style={{ fontSize: 10, color: "#A0896C" }}>{m.desc}</div>
                  </button>
                ))}
              </div>
              {dnVatMethod === "deduct" ? (<>
                <MoneyInput label="Thuế GTGT đầu ra" value={dnVatOut} onChange={setDnVatOut} placeholder="0" />
                <MoneyInput label="Thuế GTGT đầu vào được khấu trừ" value={dnVatIn} onChange={setDnVatIn} placeholder="0" />
              </>) : (
                <div style={{ fontSize: 12, color: "#5D4E37", padding: "8px 12px", background: "#E3F2FD", borderRadius: 8 }}>Thuế GTGT = Doanh thu × Tỷ lệ % theo ngành (thường 1-5%)</div>
              )}
              <div style={{ display: "flex", justifyContent: "space-between", padding: "10px 0 4px", fontWeight: 600, marginTop: 8, borderTop: "1px solid #F0E8D8" }}>
                <span style={{ fontSize: 13, color: "#3D2E1F" }}>Thuế GTGT phải nộp</span>
                <span style={{ fontSize: 15, fontFamily: "'DM Mono', monospace", color: "#1565C0" }}>{fmt(dnVat)}</span>
              </div>
            </div>

            {/* Tổng hợp thuế DN */}
            <div style={{ background: "linear-gradient(135deg, #1A237E, #283593)", borderRadius: 16, padding: 16, marginBottom: 12, color: "#FFF" }}>
              <div style={{ fontSize: 12, color: "#90CAF9", fontWeight: 600, marginBottom: 10 }}>📊 Tổng nghĩa vụ thuế doanh nghiệp</div>
              {[
                { label: "Thuế TNDN", val: dnTndn, color: "#FF8A65" },
                { label: "Thuế GTGT", val: dnVat, color: "#64B5F6" },
                { label: "Tổng cộng", val: dnTndn + dnVat, color: "#FFF", bold: true },
              ].map((item, i) => (
                <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: i < 2 ? "1px solid rgba(255,255,255,0.1)" : "none" }}>
                  <span style={{ fontSize: item.bold ? 14 : 12, color: "rgba(255,255,255,0.75)", fontWeight: item.bold ? 700 : 400 }}>{item.label}</span>
                  <span style={{ fontSize: item.bold ? 18 : 14, fontFamily: "'DM Mono', monospace", color: item.color, fontWeight: 700 }}>{fmt(item.val)}</span>
                </div>
              ))}
              <div style={{ fontSize: 10, color: "rgba(255,255,255,0.4)", marginTop: 8 }}>* Chưa bao gồm thuế TNCN cho NLĐ, lệ phí môn bài, thuế khác nếu có</div>
            </div>
          </div>)}

          {/* Các loại thuế DN phải nộp */}
          <div style={{ background: "#FFF", borderRadius: 16, padding: 16, boxShadow: "0 2px 12px rgba(0,0,0,0.04)", marginBottom: 12 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: "#3D2E1F", marginBottom: 10 }}>📋 Các loại thuế DN phải nộp 2026</div>
            {[
              { icon: "🏛️", name: "Thuế TNDN", desc: "15% / 17% / 20% trên lợi nhuận, tùy quy mô doanh thu", color: "#1A237E" },
              { icon: "🧾", name: "Thuế GTGT", desc: "Khấu trừ (ĐR-ĐV) hoặc trực tiếp (% × DT). Thuế suất: 0%, 5%, 8%, 10%", color: "#1565C0" },
              { icon: "👤", name: "Thuế TNCN (khấu trừ)", desc: "Khấu trừ thuế TNCN cho NLĐ hàng tháng, nộp thay cơ quan thuế", color: "#B8860B" },
              { icon: "📋", name: "Lệ phí môn bài", desc: "Vốn > 10 tỷ: 3tr/năm. Vốn ≤ 10 tỷ: 2tr/năm. Chi nhánh: 1tr/năm", color: "#5D4037" },
              { icon: "🔖", name: "Thuế nhà thầu (nếu có)", desc: "Áp dụng khi thanh toán cho nhà thầu nước ngoài chưa có CSTT tại VN", color: "#7B1FA2" },
            ].map((t, i) => (
              <div key={i} style={{ display: "flex", gap: 10, padding: "10px 0", borderBottom: i < 4 ? "1px solid #F0E8D8" : "none" }}>
                <span style={{ fontSize: 20, flexShrink: 0 }}>{t.icon}</span>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: t.color }}>{t.name}</div>
                  <div style={{ fontSize: 11, color: "#8B7355", lineHeight: 1.5, marginTop: 2 }}>{t.desc}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Ưu đãi */}
          <div style={{ background: "#E8F5E9", borderRadius: 14, padding: 14, border: "1px solid #A5D6A7" }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: "#2E7D32", marginBottom: 6 }}>🎁 Ưu đãi thuế TNDN 2026</div>
            <div style={{ fontSize: 11, color: "#33691E", lineHeight: 1.7 }}>
              • <b>DN nhỏ & vừa mới thành lập</b>: Miễn thuế TNDN 3 năm đầu (NĐ 20/2026)
              <br/>• <b>Khởi nghiệp đổi mới sáng tạo</b>: Miễn 2 năm + giảm 50% trong 4 năm tiếp
              <br/>• <b>Thuế suất ưu đãi 10%</b>: Áp dụng cho DN đầu tư vào địa bàn đặc biệt khó khăn, KCN, khu công nghệ cao
              <br/>• <b>Chuyển lỗ</b>: Được kết chuyển lỗ tối đa 5 năm liên tiếp
              <br/>• <b>Chi lương ≥ 5tr</b>: Phải thanh toán qua ngân hàng mới được trừ chi phí
            </div>
          </div>
        </div>)}

        {/* ===== GUIDE TAB ===== */}
        {activeTab === "guide" && (<div>
          <div style={{ display: "flex", gap: 6, marginBottom: 16 }}>
            {[{ id: "salary", label: "NLĐ", icon: "💼" }, { id: "business", label: "Hộ KD", icon: "🏪" }, { id: "enterprise", label: "Doanh nghiệp", icon: "🏢" }, { id: "investment", label: "Đầu tư", icon: "📈" }].map(g => (
              <button key={g.id} onClick={() => { setGuideType(g.id); setExpandedStep(null); }} style={{ flex: 1, padding: "10px 6px", border: guideType === g.id ? "1.5px solid #B8860B" : "1.5px solid #E8DCC8", borderRadius: 12, background: guideType === g.id ? "#FFF8E7" : "#FFF", color: guideType === g.id ? "#B8860B" : "#8B7355", fontSize: 12, fontWeight: guideType === g.id ? 600 : 400, cursor: "pointer" }}>
                <div style={{ fontSize: 18, marginBottom: 2 }}>{g.icon}</div>{g.label}
              </button>
            ))}
          </div>
          <div style={{ background: "#FFF", borderRadius: 18, padding: 16, boxShadow: "0 2px 12px rgba(0,0,0,0.04)" }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: "#3D2E1F", marginBottom: 14 }}>Hướng dẫn kê khai từng bước</div>
            {GUIDE_STEPS[guideType].map((step, i) => (
              <div key={i} style={{ marginBottom: 8 }}>
                <button onClick={() => setExpandedStep(expandedStep === i ? null : i)} style={{ width: "100%", display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", border: "1px solid #F0E8D8", borderRadius: expandedStep === i ? "12px 12px 0 0" : 12, background: expandedStep === i ? "#FFF8E7" : "#FFFDF7", cursor: "pointer", textAlign: "left" }}>
                  <div style={{ width: 28, height: 28, borderRadius: "50%", background: expandedStep === i ? "#B8860B" : "#E8DCC8", color: expandedStep === i ? "#FFF" : "#8B7355", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 700, flexShrink: 0 }}>{i + 1}</div>
                  <span style={{ fontSize: 13, fontWeight: 500, color: "#3D2E1F", flex: 1 }}>{step.title}</span>
                  <span style={{ fontSize: 16, color: "#A0896C", transform: expandedStep === i ? "rotate(180deg)" : "none", transition: "transform 0.2s" }}>▾</span>
                </button>
                {expandedStep === i && <div style={{ padding: "12px 14px 12px 50px", background: "#FFFDF7", border: "1px solid #F0E8D8", borderTop: "none", borderRadius: "0 0 12px 12px", fontSize: 13, lineHeight: 1.6, color: "#5D4E37" }}>{step.desc}</div>}
              </div>
            ))}
          </div>
          {guideType === "salary" && (
          <div style={{ background: "linear-gradient(135deg, #3D2E1F, #5D4230)", borderRadius: 16, padding: 16, marginTop: 12, color: "#FFF" }}>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 10, color: "#DAA520" }}>📌 Biểu thuế lũy tiến 2026</div>
            {TAX_BRACKETS_2026.map((b, i) => (
              <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "5px 0", borderBottom: i < 4 ? "1px solid rgba(255,255,255,0.1)" : "none" }}>
                <span style={{ fontSize: 12, color: "rgba(255,255,255,0.7)" }}>{b.max === Infinity ? `Trên ${fmtM(b.min)}` : `${fmtM(b.min)} → ${fmtM(b.max)}`}</span>
                <span style={{ fontSize: 13, fontWeight: 600, fontFamily: "'DM Mono', monospace", color: "#DAA520" }}>{b.rate * 100}%</span>
              </div>
            ))}
          </div>
          )}
        </div>)}

        {/* ===== CALENDAR TAB ===== */}
        {activeTab === "calendar" && (<div>
          {nearestDeadline && (
            <div style={{ background: daysUntil <= 7 ? "linear-gradient(135deg, #CD4500, #FF6347)" : "linear-gradient(135deg, #B8860B, #DAA520)", borderRadius: 18, padding: 20, marginBottom: 16, color: "#FFF", textAlign: "center" }}>
              <div style={{ fontSize: 12, opacity: 0.8, marginBottom: 4 }}>Hạn nộp gần nhất</div>
              <div style={{ fontSize: 32, fontWeight: 700, fontFamily: "'DM Mono', monospace", lineHeight: 1 }}>{daysUntil}</div>
              <div style={{ fontSize: 13, opacity: 0.8, marginBottom: 8 }}>ngày nữa ({nearestDeadline.date})</div>
              <div style={{ fontSize: 12, opacity: 0.9, padding: "6px 12px", background: "rgba(255,255,255,0.15)", borderRadius: 8, display: "inline-block" }}>{nearestDeadline.desc}</div>
            </div>
          )}
          <div style={{ background: "#FFF", borderRadius: 18, padding: 16, boxShadow: "0 2px 12px rgba(0,0,0,0.04)" }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: "#3D2E1F", marginBottom: 14 }}>Các mốc quan trọng 2026</div>
            {DEADLINES.map((d, i) => { const isPast = parseDate(d.date) < today; const isNext = !isPast && d === nearestDeadline; return (
              <div key={i} style={{ display: "flex", gap: 12, padding: "10px 0", borderBottom: i < DEADLINES.length - 1 ? "1px solid #F0E8D8" : "none", opacity: isPast ? 0.45 : 1 }}>
                <div style={{ width: 40, flexShrink: 0, textAlign: "center" }}><div style={{ fontSize: 11, color: isNext ? "#B8860B" : "#A0896C", fontWeight: 600 }}>{d.date.split("/")[0]}/{d.date.split("/")[1]}</div></div>
                <div style={{ flex: 1 }}><div style={{ fontSize: 12, color: isNext ? "#3D2E1F" : "#5D4E37", fontWeight: isNext ? 600 : 400, lineHeight: 1.4 }}>{d.desc}</div></div>
                <div style={{ padding: "2px 8px", borderRadius: 6, fontSize: 10, fontWeight: 500, alignSelf: "center", background: isPast ? "#E8E8E8" : d.type === "quyettoan" ? "#FFE4B5" : "#E8F5E9", color: isPast ? "#999" : d.type === "quyettoan" ? "#8B4513" : "#2E7D32" }}>{isPast ? "Đã qua" : d.type === "quyettoan" ? "QT" : "Khai"}</div>
              </div>
            ); })}
          </div>
          <div style={{ background: "#FFF8E7", borderRadius: 14, padding: 14, marginTop: 12, border: "1px solid #E8DCC8" }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: "#8B4513", marginBottom: 6 }}>💡 Lưu ý quan trọng</div>
            <div style={{ fontSize: 12, color: "#5D4E37", lineHeight: 1.6 }}>Cá nhân tự quyết toán thuế TNCN năm 2025 có hạn đến 30/04/2026. Ủy quyền cho doanh nghiệp thì hạn 31/03/2026. Nộp chậm bị phạt 0.03%/ngày trên số thuế chậm nộp.</div>
          </div>
        </div>)}
      </div>

      <div style={{ padding: "12px 20px 20px", textAlign: "center", fontSize: 10, color: "#B8A88A", lineHeight: 1.5 }}>
        Công cụ mang tính tham khảo • Nghị định 141/2026/NĐ-CP & Thông tư 50/2026/TT-BTC
        <br />Ngưỡng 1 tỷ áp dụng từ 01/01/2026 • Không thay thế tư vấn chuyên nghiệp
      </div>
    </div>
  );
}
